import React, { useState, useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import T2TTopNav from "../Layout/T2TTopNav";
import { getData, postData, putData, deleteData } from "../../../FetchApiServices";
import { useToast, useConfirm, usePermissions } from "../Layout/T2TUIProvider";
import T2TGuidanceProvider from "../Guidance/T2TGuidanceProvider";
import T2THelpPanel from "../Guidance/T2THelpPanel";
import T2TWelcomeOverlay from "../Guidance/T2TWelcomeOverlay";
import T2TTemplateGallery from "../Guidance/T2TTemplateGallery";
import T2TGuidedWizard from "../Guidance/T2TGuidedWizard";
import T2TTutorial, { emitTutorialEvent } from "../Guidance/T2TTutorial";
import { Hint, HintQueueProvider } from "../Guidance/T2THints";

const NODE_W = 210;

// ─── Formula Editor (Phase 8) ────────────────────────────────────────────────
// Renders a textarea + clickable field chips. Clicking a chip inserts {field_id}
// at the cursor position. Uses ref + selectionStart for precise insertion.
const FormulaInput = ({ value, fields, onChange }) => {
  const ref = useRef(null);
  const insertField = (fieldId) => {
    const ta  = ref.current;
    const tag = `{${fieldId}}`;
    if (!ta) { onChange(value + tag); return; }
    const start = ta.selectionStart;
    const end   = ta.selectionEnd;
    const next  = value.substring(0, start) + tag + value.substring(end);
    onChange(next);
    // Restore cursor after the inserted tag
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + tag.length;
    });
  };
  return (
    <div style={{ flex: 3, minWidth: 180 }}>
      <textarea
        ref={ref}
        className="t2t-fc"
        rows="2"
        style={{ fontFamily: "monospace", fontSize: 12 }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. {amount} * {quantity} > 500000"
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
        {fields.filter((f) => ["number", "currency"].includes(f.type)).map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => insertField(f.id)}
            style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, border: "1px solid #CBD5E1", background: "#F8FAFC", cursor: "pointer", color: "#334155", fontFamily: "monospace" }}
          >
            {`{${f.id}}`}
          </button>
        ))}
        {fields.filter((f) => !["number", "currency"].includes(f.type)).length > 0 && fields.filter((f) => ["number", "currency"].includes(f.type)).length === 0 && (
          <span style={{ fontSize: 10, color: "#94A3B8" }}>Add number/currency fields to use in formulas</span>
        )}
      </div>
      <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>
        Operators: + - * / &gt; &lt; &gt;= &lt;= == != &nbsp; Functions: round() ceil() floor() abs() min() max() if()
      </div>
    </div>
  );
};
FormulaInput.propTypes = {
  value:    PropTypes.string.isRequired,
  fields:   PropTypes.array.isRequired,
  onChange:  PropTypes.func.isRequired,
};

const STAGE_TYPES = [
  { type: "approval",       label: "Approval",       desc: "Human approval step",                    color: "#2563EB" },
  { type: "review",         label: "Review",         desc: "Review & feedback",                      color: "#D97706" },
  { type: "notification",   label: "Notification",   desc: "Notify stakeholders",                    color: "#16A34A" },
  { type: "decision",       label: "Decision",       desc: "Branch / condition",                     color: "#7C3AED" },
  { type: "parallel",       label: "Parallel",       desc: "Run sub-stages together",                color: "#0F766E" },
  // ARCH-05a: System Action stage — invokes an integration (SAP, PAN check, etc.) at runtime (executor in 05b).
  { type: "system_action",  label: "System Action",  desc: "Automated integration (SAP / PAN / ...)", color: "#0891B2" },
  { type: "end",            label: "End",            desc: "Terminal node",                          color: "#1B3A6B" },
];

// ARCH-05a: System Action subtypes. Each one has a narrow config shape the
// runtime (05b) will dispatch on. The UI in the Properties panel renders a
// different mini-form per subtype.
const SYSTEM_ACTION_SUBTYPES = [
  { value: "sap_sync",         label: "SAP — Sync record",        icon: "⚡", kind: "push" },
  { value: "ariba_push",       label: "Ariba — Push document",    icon: "📦", kind: "push" },
  { value: "fiori_sync",       label: "Fiori — Callback",         icon: "🔔", kind: "push" },
  { value: "pan_validate",     label: "PAN — Validate",           icon: "🪪", kind: "validator" },
  { value: "gst_validate",     label: "GSTIN — Validate",         icon: "🧾", kind: "validator" },
  { value: "three_way_match",  label: "3-Way Match (PO/GRN/Inv)", icon: "⚖️", kind: "validator" },
  { value: "duplicate_check",  label: "Duplicate Check",           icon: "♎", kind: "validator" },
];

const FIELD_TYPES = [
  { type: "text",     icon: "T",  label: "Text" },
  { type: "number",   icon: "#",  label: "Number" },
  { type: "currency", icon: "₹",  label: "Currency" },
  { type: "dropdown", icon: "▾",  label: "Dropdown" },
  { type: "yes_no",   icon: "✓",  label: "Yes/No" },
  { type: "date",     icon: "📅", label: "Date" },
  { type: "textarea", icon: "≡",  label: "Text Area" },
  { type: "file",     icon: "📎", label: "File Upload" },
];

const getStageColor = (type) => STAGE_TYPES.find((s) => s.type === type)?.color || "#94A3B8";

// ── Multi-select Request Type picker ──────────────────────────────────────────
// onChange fires on every checkbox click (instant local state, no API).
// onSave fires when Done is clicked or dropdown closes via outside-click.
function RequestTypeMultiPicker({ requestTypes, selectedIds, onChange, onSave, disabled }) {
  const [open, setOpen] = React.useState(false);
  const ref             = React.useRef();
  // Always-current mirror of selectedIds (avoids stale closures in event handlers)
  const selectedIdsRef  = React.useRef(selectedIds);
  // Tracks the last value that was successfully committed via onSave
  const savedIdsRef     = React.useRef(selectedIds);

  React.useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);

  // Keep onSave in a ref so commitAndClose can be stable (empty deps).
  const onSaveRef = React.useRef(onSave);
  React.useEffect(() => { onSaveRef.current = onSave; }, [onSave]);

  // When dropdown closes, re-baseline savedIdsRef so the next open is diff-checked correctly.
  React.useEffect(() => {
    if (!open) { savedIdsRef.current = selectedIdsRef.current; }
  }, [open]);

  // Commit: compare current vs saved, call onSave only if truly changed, then close.
  // Uses only refs → stable callback, no stale closures.
  const commitAndClose = React.useCallback(() => {
    const current = selectedIdsRef.current;
    const prev    = savedIdsRef.current;
    const changed = prev.length !== current.length || current.some((id) => !prev.includes(id));
    setOpen(false);
    if (changed && onSaveRef.current) {
      savedIdsRef.current = current;
      onSaveRef.current(current);
    }
  }, []); // empty deps — reads everything from refs

  // Outside-click closes and commits; effect only active while open
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) commitAndClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, commitAndClose]);

  const selectedSet = new Set(selectedIds);
  const toggle = (id) => {
    const next = new Set(selectedSet);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(Array.from(next));
  };
  const clearAll = () => onChange([]);

  const label = selectedIds.length === 0
    ? "— None (per-workflow fields) —"
    : selectedIds.length === 1
      ? (requestTypes.find((rt) => rt.request_type_id === selectedIds[0])?.name || "1 type")
      : `${selectedIds.length} types selected`;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        className="t2t-fc"
        style={{ padding: "4px 10px", fontSize: 12, height: 28, minWidth: 180, textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ fontSize: 10, flexShrink: 0 }}>▾</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, zIndex: 9999, background: "white", border: "1px solid #E2E8F0", borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", minWidth: 240, maxHeight: 240, overflowY: "auto" }}>
          <div style={{ padding: "6px 12px 4px", fontSize: 11, color: "#64748B", fontWeight: 600, borderBottom: "1px solid #F1F5F9", position: "sticky", top: 0, background: "white", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>Select request types</span>
            <button type="button" onClick={commitAndClose}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, lineHeight: 1, color: "#2563EB", padding: "2px 8px", borderRadius: 4, border: "1px solid #2563EB" }}>✓ Done</button>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", cursor: "pointer", fontSize: 12, color: "#64748B", borderBottom: "1px solid #F8FAFC" }}>
            <input type="checkbox" checked={selectedIds.length === 0} onChange={clearAll} />
            None (per-workflow fields)
          </label>
          {requestTypes.length === 0 && (
            <div style={{ padding: "8px 14px", fontSize: 12, color: "#9CA3AF" }}>No request types available</div>
          )}
          {requestTypes.map((rt) => (
            <label key={rt.request_type_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13 }}>
              <input type="checkbox" checked={selectedSet.has(rt.request_type_id)} onChange={() => toggle(rt.request_type_id)} />
              {rt.name}{rt.is_system === 1 ? <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700, color: "#1B3A6B", background: "#DBEAFE", padding: "1px 5px", borderRadius: 4 }}>SYS</span> : null}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

RequestTypeMultiPicker.propTypes = {
  requestTypes: PropTypes.arrayOf(PropTypes.shape({
    request_type_id: PropTypes.number,
    name:            PropTypes.string,
    is_system:       PropTypes.number,
  })).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange:    PropTypes.func.isRequired,
  onSave:      PropTypes.func.isRequired,
  disabled:    PropTypes.bool,
};
RequestTypeMultiPicker.defaultProps = { disabled: false };

// ── BUG-37: Specific Users section for Stage Properties ──────────────────────
// Lets admins pre-assign named users to a stage in addition to (or instead of)
// role-based assignment. When users.length > 0, runtime stage-assignment
// resolution picks these users specifically (overrides role-based matching).
const SpecificUsersSection = ({
  availableUsers, availableRoles, selectedUserIds, onToggleUser, onReplaceUsers,
}) => {
  const [search, setSearch] = useState("");
  const [bulkRoleId, setBulkRoleId] = useState("");

  // Build a fast role-id → role-name lookup for the chip label per user
  const roleNameOf = (rid) => (availableRoles.find((r) => r.role_id === rid)?.name || "");

  const filtered = !search
    ? availableUsers
    : availableUsers.filter((u) => {
        const q = search.toLowerCase();
        return (u.name || "").toLowerCase().includes(q)
            || (u.email || "").toLowerCase().includes(q)
            || roleNameOf(u.t2t_role_id).toLowerCase().includes(q);
      });

  const selectedCount = selectedUserIds.length;
  const allInView = filtered.length > 0 && filtered.every((u) => selectedUserIds.includes(u.user_id));

  const onSelectAll = () => {
    if (allInView) {
      // Remove all visible users from selection
      const visibleIds = new Set(filtered.map((u) => u.user_id));
      onReplaceUsers(selectedUserIds.filter((id) => !visibleIds.has(id)));
    } else {
      // Add every visible user to selection (dedup)
      const merged = new Set([...selectedUserIds, ...filtered.map((u) => u.user_id)]);
      onReplaceUsers([...merged]);
    }
  };

  const onClearAll = () => onReplaceUsers([]);

  const onAddAllFromRole = (roleId) => {
    if (!roleId) return;
    const idsForRole = availableUsers
      .filter((u) => String(u.t2t_role_id) === String(roleId))
      .map((u) => u.user_id);
    const merged = new Set([...selectedUserIds, ...idsForRole]);
    onReplaceUsers([...merged]);
    setBulkRoleId("");
  };

  return (
    <div className="t2t-props-sec">
      <div className="t2t-props-sec-title">Specific Users</div>
      <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginBottom: 8, lineHeight: 1.4 }}>
        Optional. When set, <strong>only these users</strong> can act on this stage — overrides role-based assignment above.
        Leave empty to fall back to role-based matching.
      </div>

      {/* Counter + select-all/clear strip */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--t2t-text-secondary)" }}>
          {selectedCount} user{selectedCount !== 1 ? "s" : ""} pre-assigned
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          <button
            type="button"
            onClick={onSelectAll}
            disabled={filtered.length === 0}
            style={{
              background: "transparent", border: "1px solid var(--t2t-border)",
              borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
              cursor: filtered.length === 0 ? "not-allowed" : "pointer",
              color: "var(--t2t-text-secondary)",
              opacity: filtered.length === 0 ? 0.4 : 1,
            }}
            title={allInView ? "Deselect all visible users" : "Select every visible user"}
          >
            {allInView ? "✕ Deselect visible" : "✓ Select visible"}
          </button>
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={onClearAll}
              style={{
                background: "transparent", border: "1px solid var(--t2t-border)",
                borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                cursor: "pointer", color: "var(--t2t-danger)",
              }}
              title="Clear all selected users"
            >
              ✕ Clear all
            </button>
          )}
        </div>
      </div>

      {/* Search + bulk-add-from-role */}
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <input
          className="t2t-fc"
          type="search"
          placeholder="Search users…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 2, fontSize: 12 }}
        />
        <select
          className="t2t-fc"
          value={bulkRoleId}
          onChange={(e) => onAddAllFromRole(e.target.value)}
          style={{ flex: 1, fontSize: 11 }}
          title="Add every user holding the selected role"
        >
          <option value="">+ All from role…</option>
          {availableRoles.map((r) => (
            <option key={r.role_id} value={r.role_id}>{r.name}</option>
          ))}
        </select>
      </div>

      {/* User list */}
      {availableUsers.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>No users available.</div>
      ) : filtered.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", textAlign: "center", padding: 8 }}>
          No users match “{search}”.
        </div>
      ) : (
        <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid var(--t2t-border)", borderRadius: 6 }}>
          {filtered.map((u) => {
            const isSelected = selectedUserIds.includes(u.user_id);
            const roleName = roleNameOf(u.t2t_role_id);
            return (
              <div
                key={u.user_id}
                onClick={() => onToggleUser(u.user_id)}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "7px 10px", cursor: "pointer",
                  background: isSelected ? "#F0FDF4" : "#fff",
                  borderBottom: "1px solid var(--t2t-border)",
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  style={{ pointerEvents: "none", flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--t2t-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {u.name}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {u.email}{roleName && <span style={{ marginLeft: 6, padding: "0 5px", background: "#F1F5F9", borderRadius: 3, color: "#475569", fontWeight: 600 }}>{roleName}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
SpecificUsersSection.propTypes = {
  availableUsers:  PropTypes.array.isRequired,
  availableRoles:  PropTypes.array.isRequired,
  selectedUserIds: PropTypes.array.isRequired,
  onToggleUser:    PropTypes.func.isRequired,
  onReplaceUsers:  PropTypes.func.isRequired,
};

// ── BUG-39: Field Mapping row editor for SAP / Ariba / Fiori System Action stages
// Replaces the textarea that silently dropped empty lines and auto-appended "="
// to whatever you typed. Internal state is an ARRAY of [k, v] tuples so empty
// rows can persist mid-edit. On every change we emit the object form to the
// parent so backend contract stays {key: value}.
const FieldMappingEditor = ({ mapping, formFields, onChange }) => {
  // Initial rows derived from the prop. We keep this in local state so that
  // adding a blank row doesn't immediately get filtered out by the
  // object-conversion roundtrip.
  const [rows, setRows] = useState(() => {
    const entries = Object.entries(mapping || {});
    return entries.length > 0 ? entries.map(([k, v]) => [k, v]) : [["", ""]];
  });

  // If the parent mapping ever resets (e.g., stage type changed), re-seed our rows.
  // We hash the incoming keys to detect external mutations without thrashing on
  // self-induced updates.
  const incomingHash = JSON.stringify(mapping || {});
  const seededHashRef = useRef(incomingHash);
  useEffect(() => {
    if (seededHashRef.current !== incomingHash) {
      const entries = Object.entries(mapping || {});
      setRows(entries.length > 0 ? entries.map(([k, v]) => [k, v]) : [["", ""]]);
      seededHashRef.current = incomingHash;
    }
  }, [incomingHash, mapping]);

  // Push the object form of rows up to the parent.
  // Empty keys are stripped at this boundary only — the local array still
  // shows the blank row so the user can keep typing.
  const emit = (nextRows) => {
    const out = {};
    nextRows.forEach(([k, v]) => {
      const key = (k || "").trim();
      if (key) out[key] = v == null ? "" : String(v);
    });
    // Update seededHash so the parent's response (same object) doesn't re-seed
    seededHashRef.current = JSON.stringify(out);
    onChange(out);
  };

  const updateRow = (idx, newKey, newVal) => {
    const next = rows.map((r, i) => (i === idx ? [newKey, newVal] : r));
    setRows(next);
    emit(next);
  };
  const addRow = () => {
    const next = [...rows, ["", ""]];
    setRows(next);
    // No emit — empty row contributes nothing to the object form
  };
  const removeRow = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    const safe = next.length === 0 ? [["", ""]] : next;
    setRows(safe);
    emit(safe);
  };

  return (
    <>
      {rows.map(([k, v], idx) => (
        <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 6, marginBottom: 6, alignItems: "center" }}>
          <input
            className="t2t-fc"
            type="text"
            list={`ff-list-${idx}`}
            placeholder="form field key (e.g. vendor_name)"
            value={k}
            onChange={(e) => updateRow(idx, e.target.value, v)}
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}
          />
          <datalist id={`ff-list-${idx}`}>
            {formFields.map((ff) => (
              <option key={ff.id || ff.field_key} value={ff.field_key || ff.id}>
                {ff.label || ff.field_key}
              </option>
            ))}
          </datalist>

          <input
            className="t2t-fc"
            type="text"
            placeholder="external field (e.g. LFA1-NAME1)"
            value={v}
            onChange={(e) => updateRow(idx, k, e.target.value)}
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}
          />

          <button
            type="button"
            onClick={() => removeRow(idx)}
            title="Remove this mapping"
            disabled={rows.length === 1 && !k && !v}
            style={{
              background: "transparent",
              border: "1px solid var(--t2t-border)",
              borderRadius: 4,
              padding: "4px 8px",
              cursor: rows.length === 1 && !k && !v ? "not-allowed" : "pointer",
              color: "var(--t2t-danger)",
              fontSize: 12,
              opacity: rows.length === 1 && !k && !v ? 0.3 : 1,
            }}
          >×</button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        style={{
          background: "transparent",
          border: "1px dashed var(--t2t-border)",
          borderRadius: 4,
          padding: "4px 10px",
          cursor: "pointer",
          fontSize: 11,
          fontWeight: 600,
          color: "var(--t2t-text-secondary)",
          marginTop: 2,
        }}
      >+ Add mapping</button>
    </>
  );
};
FieldMappingEditor.propTypes = {
  mapping:    PropTypes.object.isRequired,
  formFields: PropTypes.array.isRequired,
  onChange:   PropTypes.func.isRequired,
};

const T2TBuilder = () => {
  const navigate      = useNavigate();
  const { id: wfId }  = useParams();
  const toast              = useToast();
  const confirm            = useConfirm();
  const { hasPermission }  = usePermissions();

  const [wfName,       setWfName]       = useState("Loading...");
  const [wfStatus,     setWfStatus]     = useState("draft");
  const [wfGroupId,    setWfGroupId]    = useState(null);
  const [wfCompanyId,  setWfCompanyId]  = useState(null);
  const [activeTab,    setActiveTab]    = useState("canvas");
  const [nodes,        setNodes]        = useState([]);
  const [conns,        setConns]        = useState([]);
  const [formFields,   setFormFields]   = useState([]);
  const [selected,     setSelected]     = useState(null);
  const [connecting,   setConnecting]   = useState(null);
  const [connLine,     setConnLine]     = useState(null);
  const [propsForm,    setPropsForm]    = useState({});
  const [saving,       setSaving]       = useState(false);
  const [pageLoading,  setPageLoading]  = useState(true);
  const [pageError,    setPageError]    = useState("");
  const [publishModal,    setPublishModal]    = useState(false);
  const [publishNote,     setPublishNote]     = useState("");
  const [availableRoles,  setAvailableRoles]  = useState([]);
  const [availableUsers,  setAvailableUsers]  = useState([]);
  // fix #2b-i: Request Types — when a workflow is attached to a request type, its
  // fields come from the type (read-only in the builder) instead of per-workflow.
  const [requestTypes,          setRequestTypes]          = useState([]);
  const [attachedRequestType,   setAttachedRequestType]   = useState(null);   // first/primary (for form-field display)
  const [attachedRequestTypeIds,setAttachedRequestTypeIds] = useState([]);     // all selected ids (multi-select)
  const [attachedRequestTypesAll,setAttachedRequestTypesAll] = useState([]);  // full objects with fields
  const [activeRtTabId,         setActiveRtTabId]         = useState(null);   // which tab is active in form-fields view
  const [stageStack,      setStageStack]      = useState([]); // [{id, name}, ...] for drill-down breadcrumb
  const [testModal,       setTestModal]       = useState({ open: false, values: {}, result: null, simulating: false });
  const [condModal,       setCondModal]       = useState({ open: false, connId: null, fromLabel: "", toLabel: "", conditions: { logic: "AND", rules: [] } });
  const [rejectionRules,  setRejectionRules]  = useState([]);  // per-selected-stage rejection routing rules
  const [editingFieldId,  setEditingFieldId]  = useState(null);
  const [historyModal,    setHistoryModal]    = useState({ open: false, versions: [], loading: false });
  const [healthModal,     setHealthModal]     = useState({ open: false, issues: [] });
  const [propsOpen,       setPropsOpen]       = useState(false);
  const [helpOpen,        setHelpOpen]        = useState(false);
  const [galleryOpen,     setGalleryOpen]     = useState(false);
  const [wizardOpen,      setWizardOpen]      = useState(false);
  const [tutorialActive,  setTutorialActive]  = useState(false);

  // Refs
  const canvasRef       = useRef(null);
  const propsPanelRef   = useRef(null);
  // ARCH-04 follow-up: auto-scroll the canvas to show existing stages on first load,
  // in case they were saved at unusual coordinates. Ref guards against re-running on
  // every re-render; it only fires once per workflow-id.
  const initialScrollDoneRef = useRef(null);
  const nodeHeightsRef  = useRef({});
  const draggingNode    = useRef(null);
  const dragOffset      = useRef({ x: 0, y: 0 });
  const dragMoved       = useRef(false);
  const palDragType     = useRef(null);
  const connectingRef   = useRef(null);
  const nodesRef        = useRef(nodes);
  const isPanning       = useRef(false);
  const panStart        = useRef({ x: 0, y: 0 });

  useEffect(() => { connectingRef.current = connecting; }, [connecting]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);

  // Scroll properties panel to top whenever selection changes
  useEffect(() => {
    if (propsPanelRef.current) propsPanelRef.current.scrollTop = 0;
  }, [selected]);

  // ARCH-04 follow-up: once the workflow is loaded, scroll the canvas so the
  // saved stages are in view. Fixes the "canvas looks empty, scroll down 1200px
  // to find stages" bug when positions were saved at unusual coordinates.
  // Runs once per workflow-id.
  useEffect(() => {
    if (pageLoading) return;
    if (!canvasRef.current) return;
    if (nodes.length === 0) return;
    if (initialScrollDoneRef.current === wfId) return;

    const parentId = stageStack.length > 0 ? stageStack[stageStack.length - 1].id : null;
    const visible = nodes.filter((n) => parentId ? n.parentId === parentId : n.parentId == null);
    if (visible.length === 0) return;

    const minX = Math.min(...visible.map((n) => Number(n.x) || 0));
    const minY = Math.min(...visible.map((n) => Number(n.y) || 0));
    const PAD = 40;
    canvasRef.current.scrollLeft = Math.max(0, minX - PAD);
    canvasRef.current.scrollTop  = Math.max(0, minY - PAD);
    initialScrollDoneRef.current = wfId;
  }, [pageLoading, nodes, stageStack, wfId]);

  // Measure node heights after every render — trigger re-render if changed
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    let changed = false;
    nodes.forEach((n) => {
      const el = document.getElementById(`t2t-node-${n.id}`);
      if (el) {
        const h = el.offsetHeight;
        if (nodeHeightsRef.current[n.id] !== h) {
          nodeHeightsRef.current[n.id] = h;
          changed = true;
        }
      }
    });
    if (changed) forceUpdate((c) => c + 1);
  });

  // Escape cancels connect
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setConnecting(null); setConnLine(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ---- Fetch T2T roles & users for properties panel ----
  // fix #2b-i: also fetch Request Types for the attachment picker.
  useEffect(() => {
    const fetchRequestTypes = async () => {
      const resp = await getData("t2t/request-types");
      if (!resp.error) {
        const raw  = resp.data?.data;
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setRequestTypes(list.filter((rt) => rt.status === 1));
      }
    };
    fetchRequestTypes();
    const fetchRoles = async () => {
      const resp = await getData("t2t/roles");
      if (!resp.error) {
        const raw  = resp.data?.data;
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setAvailableRoles(list);
      }
    };
    const fetchUsers = async () => {
      const resp = await getData("users?is_t2t=true");
      if (!resp.error) {
        const raw  = resp.data?.data;
        const list = Array.isArray(raw) ? raw : [];
        setAvailableUsers(list);
      }
    };
    fetchRoles();
    fetchUsers();
  }, []);

  // ---- Load workflow on mount ----
  useEffect(() => {
    if (!wfId || wfId === "new") { setPageLoading(false); return; }
    loadWorkflow();
  }, [wfId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWorkflow = async () => {
    setPageLoading(true);
    setPageError("");
    const resp = await getData(`t2t/workflows/${wfId}`);
    if (resp.error) {
      setPageError(resp.error.message || "Failed to load workflow.");
      setPageLoading(false);
      return;
    }
    const wf = resp.data?.data;
    setWfName(wf.name || "Untitled");
    setWfStatus(wf.workflow_status || "draft");
    setWfGroupId(wf.group_id || wf.t2t_group_id || null);
    setWfCompanyId(wf.company_id || wf.t2t_company_id || null);

    // Auto-layout via BFS when positions are missing
    const computeAutoLayout = (stages, transitions) => {
      const incoming = new Set(transitions.map((t) => t.to_stage_id));
      const startId  = stages.find((s) => !incoming.has(s.stage_id))?.stage_id ?? stages[0]?.stage_id;
      const levels   = {};
      const visited  = new Set();
      const queue    = [{ id: startId, level: 0 }];
      while (queue.length) {
        const { id, level } = queue.shift();
        if (visited.has(id)) continue;
        visited.add(id);
        if (!levels[level]) levels[level] = [];
        levels[level].push(id);
        transitions.filter((t) => t.from_stage_id === id).forEach((t) => {
          if (!visited.has(t.to_stage_id)) queue.push({ id: t.to_stage_id, level: level + 1 });
        });
      }
      // Place disconnected stages at extra levels
      stages.forEach((s) => {
        if (!visited.has(s.stage_id)) {
          const max = Math.max(0, ...Object.keys(levels).map(Number)) + 1;
          if (!levels[max]) levels[max] = [];
          levels[max].push(s.stage_id);
        }
      });
      const pos = {};
      Object.entries(levels).forEach(([lvl, ids]) => {
        ids.forEach((id, i) => {
          pos[id] = { x: 160 + Number(lvl) * 290, y: 120 + i * 200 };
        });
      });
      return pos;
    };

    const stages      = wf.stages || [];
    const transitions = wf.transitions || [];
    // Always compute auto-layout as fallback for stages with no saved position
    const autoPos = computeAutoLayout(stages, transitions);

    const loadedNodes = stages.map((s, idx) => ({
      id:                    s.stage_id,
      type:                  s.stage_type || "approval",
      label:                 s.name,
      x:                     (s.x != null && s.x !== 0) ? Number(s.x) : (autoPos[s.stage_id]?.x ?? 160 + idx * 290),
      y:                     (s.y != null && s.y !== 0) ? Number(s.y) : (autoPos[s.stage_id]?.y ?? 120 + idx * 200),
      roles:                 s.role_ids || [],
      users:                 s.user_ids || [],
      sla:                   s.sla_hours ? String(s.sla_hours) : "",
      color:                 getStageColor(s.stage_type || "approval"),
      escalation_l1_before_mins: s.escalation_l1_before_mins ? String(s.escalation_l1_before_mins) : "",
      escalation_l1_before_to:   s.escalation_l1_before_to || { roles: [], users: [] },
      escalation_l1_after_mins:  s.escalation_l1_after_mins ? String(s.escalation_l1_after_mins) : "",
      escalation_l1_after_to:    s.escalation_l1_after_to || { roles: [], users: [] },
      escalation_l2_before_mins: s.escalation_l2_before_mins ? String(s.escalation_l2_before_mins) : "",
      escalation_l2_before_to:   s.escalation_l2_before_to || { roles: [], users: [] },
      escalation_l2_after_mins:  s.escalation_l2_after_mins ? String(s.escalation_l2_after_mins) : "",
      escalation_l2_after_to:    s.escalation_l2_after_to || { roles: [], users: [] },
      escalation_l3_before_mins: s.escalation_l3_before_mins ? String(s.escalation_l3_before_mins) : "",
      escalation_l3_before_to:   s.escalation_l3_before_to || { roles: [], users: [] },
      escalation_l3_after_mins:  s.escalation_l3_after_mins ? String(s.escalation_l3_after_mins) : "",
      escalation_l3_after_to:    s.escalation_l3_after_to || { roles: [], users: [] },
      completion_rule:       s.completion_rule || "all",
      completion_n:          s.completion_n ?? null,
      parentId:              s.parent_stage_id ?? null,
      field_visibility:      s.field_visibility || { visible: [], editable: [] },
      // ARCH-05a
      system_action_type:    s.system_action_type   || null,
      system_action_config:  s.system_action_config || null,
    }));

    const loadedConns = transitions.map((t) => ({
      id:     t.transition_id,
      from:   t.from_stage_id,
      to:     t.to_stage_id,
      label:      t.label || "",
      action:     t.action || "approve",
      conditions: (t.conditions && t.conditions.rules) ? t.conditions : null,
    }));

    const mapField = (f) => ({
      id:          f.field_id,
      type:        f.field_type,
      label:       f.label,
      field_id:    f.field_key || f.field_id_slug || toSlug(f.label || ""),
      placeholder: f.placeholder || "",
      required:    !!f.is_required,
      options:     f.options || [],
    });

    const loadedFields = (wf.form_fields || []).map(mapField);

    setNodes(loadedNodes);
    setConns(loadedConns);

    // fix #2b-i: if workflow is attached to request types, its fields come from the type(s).
    const allTypes = Array.isArray(wf.request_types) ? wf.request_types : (wf.request_type ? [wf.request_type] : []);
    setAttachedRequestTypeIds(allTypes.map(rt => rt.request_type_id));
    setAttachedRequestTypesAll(allTypes);

    if (allTypes.length > 0 && Array.isArray(allTypes[0].fields)) {
      setAttachedRequestType(allTypes[0]);
      setActiveRtTabId(allTypes[0].request_type_id);
      setFormFields(allTypes[0].fields.map(mapField));
      setPageLoading(false);
      return;
    }
    setAttachedRequestType(null);
    setActiveRtTabId(null);

    if (loadedFields.length > 0) {
      setFormFields(loadedFields);
      setPageLoading(false);
    } else {
      // Main workflow response didn't include form_fields — fetch separately
      const ffResp = await getData(`t2t/workflows/${wfId}/form-fields`);
      if (!ffResp.error) {
        const raw = ffResp.data?.data;
        const fields = Array.isArray(raw) ? raw : [];
        setFormFields(fields.map(mapField));
      }
      setPageLoading(false);
    }
  };

  // ---- Port positions ----
  const getPortPos = useCallback((nodeId, side) => {
    const node = nodesRef.current.find((n) => n.id === nodeId);
    if (!node) return null;
    const h = nodeHeightsRef.current[nodeId] || 88;
    if (side === "out") return { x: node.x + NODE_W, y: node.y + h / 2 };
    return { x: node.x, y: node.y + h / 2 };
  }, []);

  const makePath = (x1, y1, x2, y2) => {
    const dx = Math.max(60, Math.abs(x2 - x1) * 0.5);
    return `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`;
  };

  const _connPath = useCallback((fromId, toId) => {
    const from = getPortPos(fromId, "out");
    const to   = getPortPos(toId,   "in");
    if (!from || !to) return "";
    return makePath(from.x, from.y, to.x, to.y);
  }, [getPortPos]);

  // ---- Palette drag-drop → create stage via API ----
  const handleCanvasDrop = async (e) => {
    e.preventDefault();
    if (!palDragType.current || !wfId || wfId === "new") return;
    const rect        = canvasRef.current.getBoundingClientRect();
    const x           = Math.round(e.clientX - rect.left + canvasRef.current.scrollLeft - NODE_W / 2);
    const y           = Math.round(e.clientY - rect.top  + canvasRef.current.scrollTop  - 45);
    const type        = palDragType.current;
    const currentParentId = stageStack.length > 0 ? stageStack[stageStack.length - 1].id : null;
    palDragType.current = null;

    const payload = {
      name:             (STAGE_TYPES.find((s) => s.type === type)?.label || type) + " Stage",
      stage_type:       type,
      x, y,
      role_ids:         [],
      user_ids:         [],
      sla_hours:        null,
      ...(currentParentId && { parent_stage_id: currentParentId }),
    };
    const resp = await postData(`t2t/workflows/${wfId}/stages`, payload);
    if (resp.error) { toast(resp.error.message || "Failed to create stage.", "error"); return; }
    const raw = resp.data?.data;
    const s = raw?.stage_id ? raw : (raw?.data || raw);
    setNodes((prev) => [...prev, {
      id:                    s.stage_id,
      type:                  s.stage_type,
      label:                 s.name,
      x:                     (s.x != null && Number(s.x) > 0) ? Number(s.x) : x,
      y:                     (s.y != null && Number(s.y) > 0) ? Number(s.y) : y,
      roles:                 s.role_ids || [],
      users:                 s.user_ids || [],
      sla:                   s.sla_hours ? String(s.sla_hours) : "",
      color:                 getStageColor(s.stage_type),
      escalation_l1_before_mins: "", escalation_l1_before_to: { roles: [], users: [] },
      escalation_l1_after_mins:  "", escalation_l1_after_to:  { roles: [], users: [] },
      escalation_l2_before_mins: "", escalation_l2_before_to: { roles: [], users: [] },
      escalation_l2_after_mins:  "", escalation_l2_after_to:  { roles: [], users: [] },
      escalation_l3_before_mins: "", escalation_l3_before_to: { roles: [], users: [] },
      escalation_l3_after_mins:  "", escalation_l3_after_to:  { roles: [], users: [] },
      completion_rule:       "all",
      parentId:              stageStack.length > 0 ? stageStack[stageStack.length - 1].id : null,
    }]);
    emitTutorialEvent("t2t-stage-added", { stage_id: s.stage_id, stage_type: s.stage_type });
  };

  // ---- Node drag — update position on mouse-up ----
  const handleNodeMouseDown = (e, nodeId) => {
    e.stopPropagation();
    const node = nodesRef.current.find((n) => n.id === nodeId);
    draggingNode.current = nodeId;
    dragMoved.current    = false;
    dragOffset.current   = { x: e.clientX - node.x, y: e.clientY - node.y };
    setSelected({ type: "node", id: nodeId });
  };

  const openNodeProps = (nodeId) => {
    const node = nodesRef.current.find((n) => n.id === nodeId);
    if (!node) return;
    setSelected({ type: "node", id: nodeId });
    setPropsForm({
      label:                 node.label,
      stage_type:            node.type,
      sla:                   node.sla || "",
      roles:                 [...(node.roles || [])],
      // BUG-77: persist specific-user selection across props open/close
      users:                 [...(node.users || [])],
      escalation_l1_before_mins: node.escalation_l1_before_mins || "",
      escalation_l1_before_to:   node.escalation_l1_before_to || { roles: [], users: [] },
      escalation_l1_after_mins:  node.escalation_l1_after_mins || "",
      escalation_l1_after_to:    node.escalation_l1_after_to || { roles: [], users: [] },
      escalation_l2_before_mins: node.escalation_l2_before_mins || "",
      escalation_l2_before_to:   node.escalation_l2_before_to || { roles: [], users: [] },
      escalation_l2_after_mins:  node.escalation_l2_after_mins || "",
      escalation_l2_after_to:    node.escalation_l2_after_to || { roles: [], users: [] },
      escalation_l3_before_mins: node.escalation_l3_before_mins || "",
      escalation_l3_before_to:   node.escalation_l3_before_to || { roles: [], users: [] },
      escalation_l3_after_mins:  node.escalation_l3_after_mins || "",
      escalation_l3_after_to:    node.escalation_l3_after_to || { roles: [], users: [] },
      completion_rule:       node.completion_rule || "all",
      fv_visible:            node.field_visibility?.visible  || [],
      fv_editable:           node.field_visibility?.editable || [],
      completion_n:          node.completion_n || "",
      // ARCH-05a: System Action fields.
      system_action_type:    node.system_action_type   || "",
      system_action_config:  node.system_action_config || {},
    });
    setPropsOpen(true);
    fetchRejectionRules(nodeId);
    emitTutorialEvent("t2t-props-opened", { node_id: nodeId });
  };

  const handleCanvasMouseMove = useCallback((e) => {
    if (isPanning.current) {
      const cx = canvasRef.current;
      if (cx) {
        cx.scrollLeft = panStart.current.x - e.clientX;
        cx.scrollTop  = panStart.current.y - e.clientY;
      }
      return;
    }
    if (draggingNode.current !== null) {
      dragMoved.current = true;
      const x = Math.max(0, e.clientX - dragOffset.current.x);
      const y = Math.max(0, e.clientY - dragOffset.current.y);
      setNodes((prev) => prev.map((n) => n.id === draggingNode.current ? { ...n, x, y } : n));
    }
    if (connectingRef.current !== null) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const mx   = e.clientX - rect.left + canvasRef.current.scrollLeft;
      const my   = e.clientY - rect.top  + canvasRef.current.scrollTop;
      const from = getPortPos(connectingRef.current, "out");
      if (from) {
        const dx = Math.max(40, Math.abs(mx - from.x) * 0.4);
        setConnLine(`M${from.x},${from.y} C${from.x + dx},${from.y} ${mx - dx},${my} ${mx},${my}`);
      }
    }
  }, [getPortPos]);

  // Save position to API on mouse-up if node was actually dragged
  const handleCanvasMouseUp = useCallback(async () => {
    isPanning.current = false;
    const nodeId = draggingNode.current;
    const moved  = dragMoved.current;
    draggingNode.current = null;
    dragMoved.current    = false;
    if (!nodeId || !moved || !wfId || wfId === "new") return;
    const node = nodesRef.current.find((n) => n.id === nodeId);
    if (!node) return;
    await putData(`t2t/workflows/${wfId}/stages/${nodeId}`, { x: Math.round(node.x), y: Math.round(node.y) });
  }, [wfId]);

  const handleCanvasMouseDown = (e) => {
    if (e.button !== 0) return;
    const target = e.target;
    if (
      target === canvasRef.current ||
      target.id === "t2t-canvas-inner" ||
      target.classList.contains("t2t-canvas-svg") ||
      target.tagName === "svg"
    ) {
      isPanning.current = true;
      const cx = canvasRef.current;
      panStart.current = { x: e.clientX + (cx ? cx.scrollLeft : 0), y: e.clientY + (cx ? cx.scrollTop : 0) };
      e.preventDefault();
    }
  };

  const fitCanvas = async () => {
    const cx = canvasRef.current;
    if (!cx) return;
    const parentId = stageStack.length > 0 ? stageStack[stageStack.length - 1].id : null;
    const displayedNodes = nodes.filter((n) => parentId ? n.parentId === parentId : n.parentId == null);
    if (displayedNodes.length === 0) return;

    const NODE_W = 210;
    const NODE_H = 140;
    const GAP_X = 80;
    const GAP_Y = 60;
    const PAD = 60;
    const ids = new Set(displayedNodes.map((n) => String(n.id)));
    const relevantConns = conns.filter((c) => ids.has(String(c.from)) && ids.has(String(c.to)));

    // BFS to assign columns (depth) — find start nodes (no incoming)
    const incomingSet = new Set(relevantConns.map((c) => String(c.to)));
    const startNodes = displayedNodes.filter((n) => !incomingSet.has(String(n.id)));
    if (startNodes.length === 0) startNodes.push(displayedNodes[0]);

    const depth = {};
    const queue = startNodes.map((n) => ({ id: String(n.id), d: 0 }));
    const visited = new Set();
    while (queue.length > 0) {
      const { id, d } = queue.shift();
      if (visited.has(id)) { depth[id] = Math.max(depth[id] || 0, d); continue; }
      visited.add(id);
      depth[id] = d;
      relevantConns.filter((c) => String(c.from) === id).forEach((c) => {
        queue.push({ id: String(c.to), d: d + 1 });
      });
    }
    // Assign orphan nodes that BFS didn't reach
    displayedNodes.forEach((n) => { if (depth[String(n.id)] === undefined) depth[String(n.id)] = 0; });

    // Group nodes by column
    const columns = {};
    displayedNodes.forEach((n) => {
      const col = depth[String(n.id)] || 0;
      if (!columns[col]) columns[col] = [];
      columns[col].push(n);
    });

    // Assign positions: left-to-right columns, vertically centered per column
    const sortedCols = Object.keys(columns).map(Number).sort((a, b) => a - b);
    const maxRows = Math.max(...sortedCols.map((c) => columns[c].length));
    const totalH = maxRows * NODE_H + (maxRows - 1) * GAP_Y;

    const newPositions = {};
    sortedCols.forEach((col, ci) => {
      const colNodes = columns[col];
      const colH = colNodes.length * NODE_H + (colNodes.length - 1) * GAP_Y;
      const offsetY = (totalH - colH) / 2; // center vertically
      colNodes.forEach((n, ri) => {
        newPositions[String(n.id)] = {
          x: PAD + ci * (NODE_W + GAP_X),
          y: PAD + offsetY + ri * (NODE_H + GAP_Y),
        };
      });
    });

    // Apply new positions to state
    setNodes((prev) => prev.map((n) => {
      const pos = newPositions[String(n.id)];
      return pos ? { ...n, x: pos.x, y: pos.y } : n;
    }));

    // Save positions to backend
    for (const nid of Object.keys(newPositions)) {
      const pos = newPositions[nid];
      await putData(`t2t/workflows/${wfId}/stages/${nid}`, { x: Math.round(pos.x), y: Math.round(pos.y) });
    }

    // Scroll to fit
    setTimeout(() => {
      cx.scrollTo({ left: 0, top: 0, behavior: "smooth" });
    }, 50);
  };

  // ---- Connect ports ----
  const handlePortOutClick = (e, nodeId) => {
    e.stopPropagation();
    setConnecting(nodeId);
    setConnLine(null);
  };

  const handlePortInClick = async (e, nodeId) => {
    e.stopPropagation();
    if (connectingRef.current === null || connectingRef.current === nodeId) return;
    const fromId = connectingRef.current;
    const already = conns.find((c) => c.from === fromId && c.to === nodeId);
    setConnecting(null);
    setConnLine(null);
    if (already || !wfId || wfId === "new") return;

    // R4 — warn if there's already an unconditional arrow from this stage
    const existingFromSame = conns.filter((c) => c.from === fromId);
    const hasUnconditional = existingFromSame.some((c) => !c.conditions?.rules?.length);
    if (hasUnconditional && existingFromSame.length > 0) {
      toast("This stage already has an unconditional arrow. Add conditions to the existing arrow to avoid ambiguous routing.", "warning");
    }

    const payload = { from_stage_id: fromId, to_stage_id: nodeId, action: "approve", label: "" };
    const resp = await postData(`t2t/workflows/${wfId}/transitions`, payload);
    if (resp.error) { toast(resp.error.message || "Failed to create transition.", "error"); return; }
    const rawT = resp.data?.data;
    const t = rawT?.transition_id ? rawT : (rawT?.data || rawT);
    setConns((prev) => [...prev, { id: t.transition_id, from: t.from_stage_id, to: t.to_stage_id, label: t.label || "", action: t.action || "approve", conditions: null }]);
    emitTutorialEvent("t2t-transition-drawn", { from: fromId, to: nodeId });
  };

  // ---- Canvas click (deselect) ----
  const handleCanvasClick = (e) => {
    const inner = document.getElementById("t2t-canvas-inner");
    if (e.target === canvasRef.current || e.target === inner) {
      setSelected(null);
      setPropsOpen(false);
      setConnecting(null);
      setConnLine(null);
    }
  };

  // ---- Delete stage ----
  const deleteNode = async (nodeId) => {
    if (!wfId || wfId === "new") return;
    const ok = await confirm("All connected arrows will also be removed.", "Delete Stage?");
    if (!ok) return;
    const resp = await deleteData(`t2t/workflows/${wfId}/stages/${nodeId}`);
    if (resp.error) { toast(resp.error.message || "Failed to delete stage.", "error"); return; }
    setNodes((prev) => prev.filter((n) => n.id !== nodeId));
    setConns((prev) => prev.filter((c) => c.from !== nodeId && c.to !== nodeId));
    setSelected(null);
  };

  // ---- Delete transition ----
  const deleteConn = async (connId) => {
    if (!wfId || wfId === "new") return;
    const resp = await deleteData(`t2t/workflows/${wfId}/transitions/${connId}`);
    if (resp.error) { toast(resp.error.message || "Failed to delete transition.", "error"); return; }
    setConns((prev) => prev.filter((c) => c.id !== connId));
    setSelected(null);
  };

  // ---- Save node props ----
  const saveNodeProps = async () => {
    if (!selected || !wfId || wfId === "new") return;
    setSaving(true);
    const fieldVisibility = {
      visible:  propsForm.fv_visible  || [],
      editable: propsForm.fv_editable || [],
    };
    const payload = {
      name:                  propsForm.label,
      stage_type:            propsForm.stage_type,
      sla_hours:             propsForm.sla ? Number(propsForm.sla) : null,
      // Drop stale role IDs (soft-deleted roles) — backend rejects the whole save otherwise. Skip filter if roles haven't loaded, to avoid wiping valid IDs.
      role_ids:              (propsForm.roles || [])
                               .map(Number)
                               .filter(Boolean)
                               .filter((rid) => !availableRoles.length || availableRoles.some((r) => r.role_id === rid)),
      // BUG-37: specific named users assigned to this stage (overrides role-based when non-empty)
      user_ids:              (propsForm.users || [])
                               .map(Number)
                               .filter(Boolean)
                               .filter((uid) => !availableUsers.length || availableUsers.some((u) => u.user_id === uid)),
      escalation_l1_before_mins: propsForm.escalation_l1_before_mins ? Number(propsForm.escalation_l1_before_mins) : null,
      escalation_l1_before_to:   propsForm.escalation_l1_before_to || null,
      escalation_l1_after_mins:  propsForm.escalation_l1_after_mins ? Number(propsForm.escalation_l1_after_mins) : null,
      escalation_l1_after_to:    propsForm.escalation_l1_after_to || null,
      escalation_l2_before_mins: propsForm.escalation_l2_before_mins ? Number(propsForm.escalation_l2_before_mins) : null,
      escalation_l2_before_to:   propsForm.escalation_l2_before_to || null,
      escalation_l2_after_mins:  propsForm.escalation_l2_after_mins ? Number(propsForm.escalation_l2_after_mins) : null,
      escalation_l2_after_to:    propsForm.escalation_l2_after_to || null,
      escalation_l3_before_mins: propsForm.escalation_l3_before_mins ? Number(propsForm.escalation_l3_before_mins) : null,
      escalation_l3_before_to:   propsForm.escalation_l3_before_to || null,
      escalation_l3_after_mins:  propsForm.escalation_l3_after_mins ? Number(propsForm.escalation_l3_after_mins) : null,
      escalation_l3_after_to:    propsForm.escalation_l3_after_to || null,
      completion_rule:       propsForm.completion_rule || "all",
      completion_n:          propsForm.completion_rule === "custom_n" ? Number(propsForm.completion_n) || null : null,
      field_visibility:      fieldVisibility,
      // ARCH-05a: System Action fields. Only meaningful when stage_type='system_action',
      // but we send them regardless so switching type → other clears stale config.
      system_action_type:    propsForm.stage_type === "system_action" ? (propsForm.system_action_type || null) : null,
      system_action_config:  propsForm.stage_type === "system_action" ? (propsForm.system_action_config || {}) : null,
    };
    const resp = await putData(`t2t/workflows/${wfId}/stages/${selected.id}`, payload);
    if (resp.error) { toast(resp.error.message || "Failed to save stage.", "error"); setSaving(false); return; }
    setNodes((prev) => prev.map((n) =>
      n.id === selected.id
        ? {
            ...n,
            label:                 propsForm.label,
            type:                  propsForm.stage_type,
            sla:                   propsForm.sla,
            roles:                 propsForm.roles,
            color:                 getStageColor(propsForm.stage_type),
            escalation_l1_before_mins: propsForm.escalation_l1_before_mins,
            escalation_l1_before_to:   propsForm.escalation_l1_before_to,
            escalation_l1_after_mins:  propsForm.escalation_l1_after_mins,
            escalation_l1_after_to:    propsForm.escalation_l1_after_to,
            escalation_l2_before_mins: propsForm.escalation_l2_before_mins,
            escalation_l2_before_to:   propsForm.escalation_l2_before_to,
            escalation_l2_after_mins:  propsForm.escalation_l2_after_mins,
            escalation_l2_after_to:    propsForm.escalation_l2_after_to,
            escalation_l3_before_mins: propsForm.escalation_l3_before_mins,
            escalation_l3_before_to:   propsForm.escalation_l3_before_to,
            escalation_l3_after_mins:  propsForm.escalation_l3_after_mins,
            escalation_l3_after_to:    propsForm.escalation_l3_after_to,
            completion_rule:       propsForm.completion_rule,
            completion_n:          propsForm.completion_n || null,
            field_visibility:      fieldVisibility,
            // ARCH-05a: keep node state in sync with what we just saved.
            system_action_type:    propsForm.stage_type === "system_action" ? (propsForm.system_action_type || null) : null,
            system_action_config:  propsForm.stage_type === "system_action" ? (propsForm.system_action_config || null) : null,
          }
        : n
    ));
    toast("Stage saved.", "success");
    setSaving(false);
    if ((propsForm.roles || []).length > 0) emitTutorialEvent("t2t-role-assigned", { stage_id: selected.id });
  };

  // ---- Save connection props — delete + recreate (no PUT for transitions) ----
  const saveConnProps = async () => {
    if (!selected || !wfId || wfId === "new") return;
    const conn = conns.find((c) => c.id === selected.id);
    if (!conn) return;
    setSaving(true);
    // Delete old
    await deleteData(`t2t/workflows/${wfId}/transitions/${selected.id}`);
    // Re-create with updated label/action
    const payload = { from_stage_id: conn.from, to_stage_id: conn.to, action: propsForm.action || "approve", label: propsForm.label || "", conditions: conn.conditions || null };
    const resp = await postData(`t2t/workflows/${wfId}/transitions`, payload);
    if (resp.error) { toast(resp.error.message || "Failed to save transition.", "error"); setSaving(false); return; }
    const t = resp.data?.data;
    setConns((prev) => prev.map((c) =>
      c.id === selected.id
        ? { ...c, id: t.transition_id, label: t.label || "", action: t.action || "approve", conditions: conn.conditions || null }
        : c
    ));
    setSelected({ type: "conn", id: t.transition_id });
    setSaving(false);
  };

  // ---- Rename workflow (on blur) ----
  const [wfNameConflict, setWfNameConflict] = useState("");

  const handleWfNameBlur = async () => {
    if (!wfId || wfId === "new" || !wfName.trim()) return;
    // Check 1 — duplicate workflow name validation
    const checkResp = await getData(`t2t/workflows/check-name?name=${encodeURIComponent(wfName.trim())}&exclude_id=${wfId}`);
    if (!checkResp.error) {
      const exists = checkResp.data?.data?.exists ?? false;
      if (exists) {
        setWfNameConflict("This name is already used by another workflow.");
        return; // don't save if conflict
      }
    }
    setWfNameConflict("");
    await putData(`t2t/workflows/${wfId}`, { name: wfName });
  };

  // ---- Workflow Health Check ----
  const runHealthCheck = (nodeList, connList) => {
    const issues = [];

    // 1. No stages at all
    if (nodeList.length === 0) {
      issues.push({ severity: "critical", msg: "Workflow has no stages. Add at least a Start and End stage." });
      return issues; // nothing more to check
    }

    // 2. Entry point check — stage with no incoming connections
    const hasIncoming = new Set(connList.map((c) => c.to));
    const entryNodes  = nodeList.filter((n) => !hasIncoming.has(n.id));
    if (entryNodes.length === 0) {
      issues.push({ severity: "critical", msg: "No entry point found. At least one stage must have no incoming arrows." });
    } else if (entryNodes.length > 1) {
      issues.push({ severity: "warning", msg: `Multiple entry points found: ${entryNodes.map((n) => n.label).join(", ")}. Only one stage should be the starting point.` });
    }

    // 3. End stage check
    const hasEndStage = nodeList.some((n) => n.type === "end");
    if (!hasEndStage) {
      issues.push({ severity: "critical", msg: 'No "End" stage found. Every workflow must have an End stage.' });
    }

    // 4. Orphaned stages — no connections at all (not in any arrow as from or to)
    const connectedIds = new Set([...connList.map((c) => c.from), ...connList.map((c) => c.to)]);
    const orphans = nodeList.filter((n) => !connectedIds.has(n.id));
    if (orphans.length > 0) {
      issues.push({ severity: "warning", msg: `${orphans.length} orphaned stage(s) with no connections: ${orphans.map((n) => n.label).join(", ")}.` });
    }

    // 5. Stages with no outgoing transitions (except End stage)
    const hasOutgoing = new Set(connList.map((c) => c.from));
    const deadEnds = nodeList.filter((n) => n.type !== "end" && !hasOutgoing.has(n.id));
    if (deadEnds.length > 0) {
      issues.push({ severity: "warning", msg: `Stage(s) with no outgoing arrow: ${deadEnds.map((n) => n.label).join(", ")}. These will block requests.` });
    }

    // 5b. Duplicate stage names at the same level (Check 2)
    const nameCounts = {};
    nodeList.forEach((n) => {
      const key = (n.label || "").trim().toLowerCase();
      if (key) nameCounts[key] = (nameCounts[key] || 0) + 1;
    });
    const dupNames = Object.entries(nameCounts).filter(([, c]) => c > 1).map(([name]) => name);
    if (dupNames.length > 0) {
      issues.push({ severity: "warning", msg: `Duplicate stage names: ${dupNames.join(", ")}. Rename to avoid confusion in audit trails.` });
    }

    // 6. Cycle detection — DFS
    const adjMap = {};
    nodeList.forEach((n) => { adjMap[n.id] = []; });
    connList.forEach((c) => { if (adjMap[c.from]) adjMap[c.from].push(c.to); });

    const visited  = new Set();
    const recStack = new Set();
    let cycleDetected = false;

    const dfs = (nodeId) => {
      if (recStack.has(nodeId)) { cycleDetected = true; return; }
      if (visited.has(nodeId))  return;
      visited.add(nodeId);
      recStack.add(nodeId);
      (adjMap[nodeId] || []).forEach((neighbor) => { if (!cycleDetected) dfs(neighbor); });
      recStack.delete(nodeId);
    };

    nodeList.forEach((n) => { if (!visited.has(n.id)) dfs(n.id); });

    if (cycleDetected) {
      issues.push({ severity: "critical", msg: "Cycle detected in the workflow. Stages form a loop — requests would be stuck forever." });
    }

    return issues;
  };

  // ---- Open Publish — run health check first ----
  const openPublishModal = () => {
    const issues = runHealthCheck(nodes, conns);
    const hasCritical = issues.some((i) => i.severity === "critical");
    if (issues.length > 0) {
      setHealthModal({ open: true, issues, proceedToPublish: !hasCritical });
    } else {
      setPublishModal(true);
    }
  };

  // ---- Publish ----
  const handlePublish = async () => {
    setSaving(true);
    const resp = await putData(`t2t/workflows/${wfId}/publish`, { note: publishNote });
    if (resp.error) { toast(resp.error.message || "Failed to publish.", "error"); setSaving(false); return; }
    toast("Workflow published successfully!", "success");
    setWfStatus("active");
    setPublishModal(false);
    setPublishNote("");
    setSaving(false);
    emitTutorialEvent("t2t-published", { workflow_id: wfId });
  };

  // ---- Instant local update (no API) — called on every checkbox click ----
  // Only update the IDs; don't wipe attachedRequestTypesAll so existing tabs stay visible.
  // saveRequestTypes (called on dropdown close) will refresh the full objects from server.
  const changeRequestTypeIds = (ids) => {
    setAttachedRequestTypeIds(ids);
  };

  // ---- Save to backend + refresh only the form-fields section (not the canvas) ----
  const saveRequestTypes = async (ids) => {
    if (!wfId || wfId === "new") return;
    setSaving(true);
    const resp = await putData(`t2t/workflows/${wfId}`, { request_type_ids: ids });
    if (resp.error) { toast(resp.error.message || "Failed to update request types.", "error"); setSaving(false); return; }

    // Reload only the request_types (with fields) from the workflow — don't reset canvas
    const wfResp = await getData(`t2t/workflows/${wfId}`);
    if (!wfResp.error) {
      const wf = wfResp.data?.data || wfResp.data || {};
      const allTypes = Array.isArray(wf.request_types) ? wf.request_types
        : (wf.request_type ? [wf.request_type] : []);
      setAttachedRequestTypesAll(allTypes);
      setAttachedRequestTypeIds(allTypes.map(rt => rt.request_type_id));
      if (allTypes.length > 0) {
        const primary = allTypes[0];
        setAttachedRequestType(primary);
        setActiveRtTabId(prev => allTypes.find(rt => rt.request_type_id === prev)
          ? prev : primary.request_type_id);
        const mapField = (f) => ({ id: f.field_id, type: f.field_type, label: f.label,
          field_id: f.field_key || f.field_id, placeholder: f.placeholder || "",
          required: !!f.is_required, options: f.options || [] });
        setFormFields((Array.isArray(primary.fields) ? primary.fields : []).map(mapField));
      } else {
        setAttachedRequestType(null);
        setActiveRtTabId(null);
      }
    }
    toast(ids.length > 0 ? "Request types updated" : "Request types detached", "success");
    setSaving(false);
  };

  // ---- Save form fields (bulk) ----
  const saveFormFields = async () => {
    // fix #2b-i: when attached to a request type, fields are owned by the type.
    if (attachedRequestType) {
      toast("Form fields are defined by the attached Request Type. Edit them in Admin → Request Types.", "warning");
      return;
    }
    if (!wfId || wfId === "new") return;
    setSaving(true);
    const payload = {
      fields: formFields.map((f, i) => ({
        label:       f.label,
        field_type:  f.type,
        placeholder: f.placeholder || "",
        is_required: f.required ? 1 : 0,
        sort_order:  i + 1,
        options:     f.options || [],
      })),
    };
    const resp = await putData(`t2t/workflows/${wfId}/form-fields`, payload);
    if (resp.error) { toast(resp.error.message || "Failed to save form fields.", "error"); }
    else {
      toast("Form fields saved.", "success");
      const updated = resp.data?.data;
      if (Array.isArray(updated) && updated.length > 0) {
        // Build a map of tmp-ID → real field_id for patching conditions
        const idMap = {};
        setFormFields((prev) => prev.map((f, i) => {
          const ret = updated[i];
          if (ret && String(f.id).startsWith("tmp-")) {
            idMap[String(f.id)] = ret.field_id;
            return { ...f, id: ret.field_id, field_id: ret.field_key || f.field_id };
          }
          return f;
        }));

        // Patch any conditions in conns that still reference old tmp- IDs
        if (Object.keys(idMap).length > 0) {
          const patchGroup = (group) => {
            if (!group || !group.rules) return group;
            return {
              ...group,
              rules: group.rules.map((rule) => {
                if (rule.type === "group") return patchGroup(rule);
                const newField = idMap[String(rule.field)];
                return newField ? { ...rule, field: newField } : rule;
              }),
            };
          };
          setConns((prev) => prev.map((c) =>
            c.conditions ? { ...c, conditions: patchGroup(c.conditions) } : c
          ));
        }
      }
    }
    setSaving(false);
  };

  const toSlug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/, "");

  const addFormField = (type) => {
    const label = FIELD_TYPES.find((f) => f.type === type)?.label || type;
    const tmpId = `tmp-${Date.now()}`;
    setFormFields((prev) => [...prev, { id: tmpId, type, label, field_id: toSlug(label), placeholder: "", required: false, options: [] }]);
    setEditingFieldId(tmpId);
  };
  const deleteFormField = (fid) => { setFormFields((prev) => prev.filter((f) => f.id !== fid)); setEditingFieldId(null); };

  // ---- Condition builder helpers ----
  // ARCH-04 Shape A: canonicalise field_type so Request Type fields (select/radio/email/tel)
  // map to the same operator sets as the legacy equivalents (dropdown/text).
  const canonicalFieldType = (type) => {
    const t = String(type || "text").toLowerCase();
    if (t === "select" || t === "radio")        return "dropdown";
    if (t === "yes_no" || t === "boolean")      return "yes_no";
    if (t === "tel"    || t === "email")        return "text";
    return t;
  };

  const getOperators = (rawType) => {
    const type = canonicalFieldType(rawType);
    if (type === "number" || type === "currency") return [
      { v: "eq", l: "=" }, { v: "neq", l: "≠" }, { v: "gt", l: ">" }, { v: "gte", l: "≥" }, { v: "lt", l: "<" }, { v: "lte", l: "≤" },
      { v: "between", l: "between" }, { v: "is_empty", l: "is empty" }, { v: "is_not_empty", l: "is not empty" },
      { v: "formula", l: "formula" },
    ];
    if (type === "dropdown") return [{ v: "eq", l: "is" }, { v: "neq", l: "is not" }, { v: "in", l: "is one of" }, { v: "not_in", l: "is not one of" }];
    if (type === "yes_no" || type === "checkbox") return [{ v: "is_true", l: "is Yes" }, { v: "is_false", l: "is No" }];
    if (type === "date")     return [{ v: "eq", l: "equals" }, { v: "gt", l: "after" }, { v: "lt", l: "before" }, { v: "gte", l: "on or after" }, { v: "lte", l: "on or before" }, { v: "between", l: "between" }];
    return [{ v: "eq", l: "equals" }, { v: "neq", l: "not equals" }, { v: "contains", l: "contains" }, { v: "starts_with", l: "starts with" }, { v: "in", l: "is one of" }, { v: "not_in", l: "is not one of" }, { v: "is_empty", l: "is empty" }, { v: "is_not_empty", l: "is not empty" }];
  };

  const buildCondPreview = (group) => {
    if (!group?.rules?.length) return "";
    const opMap = { eq: "is", neq: "is not", gt: ">", gte: "≥", lt: "<", lte: "≤", contains: "contains", starts_with: "starts with", is_empty: "is empty", is_not_empty: "is not empty", is_true: "is Yes", is_false: "is No", between: "is between", in: "is one of", not_in: "is not one of", formula: "formula" };
    const parts = group.rules.map((rule) => {
      if (rule.type === "group") return `(${buildCondPreview(rule)})`;
      const ff = formFields.find((f) => String(f.id) === String(rule.field));
      const fieldName = ff?.label || rule.field || "?";
      const op = opMap[rule.operator] || rule.operator;
      if (["is_empty", "is_not_empty", "is_true", "is_false"].includes(rule.operator)) return `${fieldName} ${op}`;
      if (rule.operator === "between") return `${fieldName} between ${rule.value || "?"} and ${rule.value2 || "?"}`;
      if (rule.operator === "in" || rule.operator === "not_in") return `${fieldName} ${op} [${rule.value || ""}]`;
      if (rule.operator === "formula") return `ƒ(${rule.value || "?"})`;
      return `${fieldName} ${op} ${rule.value}`;
    });
    return parts.filter(Boolean).join(` ${group.logic} `);
  };

  const renderCondGroup = (group, onGroupChange, depth = 0) => {
    if (!group || !group.rules) group = { logic: "AND", rules: [], ...group };
    const rules = group.rules || [];
    const addRule = () => {
      const newRule = { id: `r_${Date.now()}`, type: "rule", field: formFields[0] ? String(formFields[0].id) : "", operator: "eq", value: "" };
      onGroupChange({ ...group, rules: [...rules, newRule] });
    };
    const addSubGroup = () => {
      const newGroup = { id: `g_${Date.now()}`, type: "group", logic: "AND", rules: [] };
      onGroupChange({ ...group, rules: [...rules, newGroup] });
    };
    const updateItem = (idx, updated) => onGroupChange({ ...group, rules: rules.map((r, i) => i === idx ? updated : r) });
    const removeItem = (idx)          => onGroupChange({ ...group, rules: rules.filter((_, i) => i !== idx) });

    return (
      <div style={{ border: depth > 0 ? "1px solid var(--t2t-border)" : "none", borderRadius: 6, padding: depth > 0 ? 10 : 0, marginBottom: depth > 0 ? 8 : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>Match</span>
          {["AND", "OR"].map((l) => (
            <button key={l} onClick={() => onGroupChange({ ...group, logic: l })}
              style={{ padding: "2px 10px", borderRadius: 4, fontSize: 12, fontWeight: 700, border: "1px solid", cursor: "pointer",
                background: group.logic === l ? "var(--t2t-primary)" : "transparent",
                color:      group.logic === l ? "white" : "var(--t2t-text-muted)",
                borderColor: group.logic === l ? "var(--t2t-primary)" : "var(--t2t-border)" }}
            >{l}</button>
          ))}
          <span style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>of the following</span>
        </div>

        {rules.map((rule, idx) => {
          if (rule.type === "group") {
            return (
              <div key={rule.id}>
                {renderCondGroup(rule, (updated) => updateItem(idx, updated), depth + 1)}
                <button onClick={() => removeItem(idx)} style={{ fontSize: 11, color: "var(--t2t-danger)", cursor: "pointer", background: "none", border: "none", marginBottom: 8 }}>Remove Group</button>
              </div>
            );
          }
          const ff      = formFields.find((f) => String(f.id) === String(rule.field));
          const rawType = ff?.type || "text";
          const canon   = canonicalFieldType(rawType);
          const ops     = getOperators(rawType);
          const noVal     = ["is_empty", "is_not_empty", "is_true", "is_false"].includes(rule.operator);
          const isBetween = rule.operator === "between";
          const isMulti   = rule.operator === "in" || rule.operator === "not_in";
          const isFormula = rule.operator === "formula";
          const isDupRule = group.rules.filter((r) => r.type === "rule" && r.field === rule.field && r.operator === rule.operator && r.value === rule.value && r.id !== rule.id).length > 0;
          // ARCH-04 Shape A: pick the HTML input type & render shape based on the field's canonical type.
          const ffOpts    = Array.isArray(ff?.options) ? ff.options : [];
          const inputType = canon === "number" || canon === "currency" ? "number"
                          : canon === "date"                           ? "date"
                          : "text";
          const renderValueInput = (val, placeholder, onChange, extraStyle = {}) => {
            // If the field is a dropdown AND the operator expects a single value, render a <select>.
            if (canon === "dropdown" && !isMulti && ffOpts.length > 0) {
              return (
                <select className="t2t-fc" style={{ flex: 2, minWidth: 80, ...extraStyle }}
                  value={val} onChange={(e) => onChange(e.target.value)}>
                  <option value="">— pick a value —</option>
                  {ffOpts.map((o) => {
                    const v = typeof o === "object" ? (o.value || o.label || "") : o;
                    const l = typeof o === "object" ? (o.label || o.value || "") : o;
                    return <option key={String(v)} value={v}>{l}</option>;
                  })}
                </select>
              );
            }
            return (
              <input className="t2t-fc" type={inputType} style={{ flex: 2, minWidth: 80, ...extraStyle }}
                value={val} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
            );
          };
          return (
            <div key={rule.id} style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center", flexWrap: "wrap", background: isDupRule ? "#FFFBEB" : undefined, borderRadius: isDupRule ? 6 : undefined, padding: isDupRule ? "4px 6px" : undefined, border: isDupRule ? "1px solid #FDE68A" : undefined }}
              title={isDupRule ? "Duplicate rule — same field, operator and value already exists" : undefined}>
              <select className="t2t-fc" style={{ flex: 2, minWidth: 100 }} value={rule.field}
                onChange={(e) => updateItem(idx, { ...rule, field: e.target.value, operator: "eq", value: "" })}>
                <option value="">Select field</option>
                {formFields.map((f) => <option key={f.id} value={String(f.id)}>{f.label}</option>)}
              </select>
              <select className="t2t-fc" style={{ flex: 2, minWidth: 100 }} value={rule.operator}
                onChange={(e) => updateItem(idx, { ...rule, operator: e.target.value, value: "", value2: "" })}>
                {ops.map((op) => <option key={op.v} value={op.v}>{op.l}</option>)}
              </select>
              {!noVal && !isBetween && !isFormula && renderValueInput(
                rule.value,
                isMulti ? "val1, val2, val3" : "Value",
                (v) => updateItem(idx, { ...rule, value: v }),
              )}
              {isBetween && (
                <>
                  {renderValueInput(rule.value || "", "From",
                    (v) => updateItem(idx, { ...rule, value: v }),
                    { flex: 1, minWidth: 60 })}
                  <span style={{ fontSize: 11, color: "#64748B" }}>and</span>
                  {renderValueInput(rule.value2 || "", "To",
                    (v) => updateItem(idx, { ...rule, value2: v }),
                    { flex: 1, minWidth: 60 })}
                </>
              )}
              {isFormula && (
                <FormulaInput
                  value={rule.value || ""}
                  fields={formFields}
                  onChange={(val) => updateItem(idx, { ...rule, value: val })}
                />
              )}
              <button onClick={() => removeItem(idx)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--t2t-danger)", fontSize: 16, lineHeight: 1, padding: "0 4px" }}>✕</button>
            </div>
          );
        })}

        <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
          <button onClick={addRule}     style={{ fontSize: 12, color: "var(--t2t-primary)", cursor: "pointer", background: "none", border: "none", fontWeight: 600, padding: 0 }}>+ Add Condition</button>
          {depth === 0 && <button onClick={addSubGroup} style={{ fontSize: 12, color: "var(--t2t-primary)", cursor: "pointer", background: "none", border: "none", fontWeight: 600, padding: 0 }}>+ Add Group</button>}
        </div>
      </div>
    );
  };

  const openCondModal = (conn) => {
    const fromNode = nodes.find((n) => n.id === conn.from);
    const toNode   = nodes.find((n) => n.id === conn.to);
    setCondModal({
      open:       true,
      connId:     conn.id,
      fromLabel:  fromNode?.label || "?",
      toLabel:    toNode?.label   || "?",
      conditions: conn.conditions ? JSON.parse(JSON.stringify(conn.conditions)) : { logic: "AND", rules: [] },
    });
  };

  const saveConditions = () => {
    setConns((prev) => prev.map((c) => c.id === condModal.connId ? { ...c, conditions: condModal.conditions } : c));
    setCondModal((p) => ({ ...p, open: false }));
    emitTutorialEvent("t2t-condition-saved", { conn_id: condModal.connId });
  };

  // ---- Test Workflow simulation ----
  const openHistoryModal = async () => {
    setHistoryModal({ open: true, versions: [], loading: true });
    try {
      const resp = await getData(`t2t/workflows/${wfId}/versions`);
      const raw = resp.data?.data;
      const list =
        Array.isArray(raw)          ? raw          :
        Array.isArray(raw?.data)    ? raw.data     :
        Array.isArray(raw?.versions)? raw.versions :
        Array.isArray(resp.data)    ? resp.data    :
        [];
      setHistoryModal({ open: true, versions: list, loading: false });
    } catch {
      setHistoryModal({ open: true, versions: [], loading: false });
    }
  };

  const openTestModal = () => {
    const values = {};
    formFields.forEach((f) => { values[f.id] = ""; });
    setTestModal({ open: true, values, result: null, simulating: false });
  };

  const evaluateCond = (group, values) => {
    if (!group?.rules?.length) return true;
    const results = group.rules.map((rule) => {
      if (rule.type === "group") return evaluateCond(rule, values);
      const rawVal   = values[rule.field] ?? values[String(rule.field)] ?? values[Number(rule.field)] ?? "";
      const fieldVal = String(rawVal).toLowerCase().trim();
      const ruleVal  = String(rule.value  || "").toLowerCase().trim();
      switch (rule.operator) {
        case "eq":           return fieldVal === ruleVal;
        case "neq":          return fieldVal !== ruleVal;
        case "contains":     return fieldVal.includes(ruleVal);
        case "starts_with":  return fieldVal.startsWith(ruleVal);
        case "is_empty":     return fieldVal === "";
        case "is_not_empty": return fieldVal !== "";
        case "gt":           return Number(fieldVal) > Number(ruleVal);
        case "gte":          return Number(fieldVal) >= Number(ruleVal);
        case "lt":           return Number(fieldVal) < Number(ruleVal);
        case "lte":          return Number(fieldVal) <= Number(ruleVal);
        case "is_true":      return ["true", "yes", "1"].includes(fieldVal);
        case "is_false":     return ["false", "no", "0", ""].includes(fieldVal);
        case "between": {
          const num = Number(fieldVal);
          return num >= Number(rule.value) && num <= Number(rule.value2 || rule.value);
        }
        case "in":     return (rule.value || "").split(",").map((v) => v.trim().toLowerCase()).includes(fieldVal);
        case "not_in": return !(rule.value || "").split(",").map((v) => v.trim().toLowerCase()).includes(fieldVal);
        case "formula": {
          try {
            // Replace {field_id} with actual numeric values from test form
            const expr = (rule.value || "").replace(/\{(\w+)\}/g, (_, key) => Number(values[key] || 0));
            // Safe eval — only arithmetic + comparison, no access to globals
            // eslint-disable-next-line no-new-func
            return !!new Function(`"use strict"; return (${expr});`)();
          } catch { return false; }
        }
        default:             return false;
      }
    });
    return group.logic === "AND" ? results.every(Boolean) : results.some(Boolean);
  };

  const runSimulation = () => {
    setTestModal((p) => ({ ...p, simulating: true }));
    const rootNodes   = nodes.filter((n) => n.parentId == null);
    const incomingIds = new Set(conns.map((c) => String(c.to)));
    const startNode   = rootNodes.find((n) => !incomingIds.has(String(n.id))) || rootNodes[0];
    if (!startNode) {
      setTestModal((p) => ({ ...p, simulating: false, result: { error: "No stages found. Add stages to the canvas first." } }));
      return;
    }
    const path    = [];
    const visited = new Set();
    let   current = startNode;
    const values  = testModal.values;

    const debugLog = [];
    while (current && !visited.has(String(current.id))) {
      visited.add(String(current.id));
      path.push({ id: current.id, label: current.label, type: current.type, color: current.color });
      if (current.type === "end") break;

      const outgoing = conns.filter((c) => String(c.from) === String(current.id));
      if (outgoing.length === 0) {
        debugLog.push(`⚠ "${current.label}" has no outgoing connections in loaded data.`);
        break;
      }

      // Evaluate: try conditional transitions first (in order), then fall back to unconditional
      let next = null;
      let reason = "";
      for (const conn of outgoing) {
        if (conn.conditions?.rules?.length > 0 && evaluateCond(conn.conditions, values)) {
          next = conn;
          reason = `condition matched on arrow to "${nodes.find((n) => String(n.id) === String(conn.to))?.label}"`;
          break;
        }
      }
      if (!next) {
        next = outgoing.find((c) => !c.conditions?.rules?.length) || outgoing[0];
        reason = `fallback (no condition matched) → arrow to "${nodes.find((n) => String(n.id) === String(next.to))?.label}"`;
      }
      debugLog.push(`"${current.label}" → ${reason}`);
      current = nodes.find((n) => String(n.id) === String(next.to)) || null;
      if (!current) debugLog.push(`⚠ Target stage not found in loaded data (id=${next.to}).`);
    }
    setTestModal((p) => ({ ...p, simulating: false, result: { path, debugLog } }));
  };

  // ---- Sub-stage drill-down ----
  const drillIntoStage = (stageId, stageName) => {
    setStageStack((prev) => [...prev, { id: stageId, name: stageName }]);
    setSelected(null);
    setConnecting(null);
    setConnLine(null);
  };

  const drillUpTo = (index) => {
    setStageStack((prev) => prev.slice(0, index));
    setSelected(null);
    setConnecting(null);
    setConnLine(null);
  };

  // ---- Rejection Routing Rules (Phase 9) ----
  const fetchRejectionRules = async (stageId) => {
    setRejectionRules([]);
    if (!wfId || wfId === "new") return;
    const resp = await getData(`t2t/workflows/${wfId}/stages/${stageId}/rejection-rules`);
    if (!resp.error) {
      const raw = resp.data?.data;
      setRejectionRules(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
    }
  };

  const addRejectionRule = async (stageId, allowedReturnStageId, label = "") => {
    if (!stageId) return;
    const resp = await postData(`t2t/workflows/${wfId}/stages/${stageId}/rejection-rules`, {
      allowed_return_stage_id: allowedReturnStageId,
      role_id:                null,
      label:                  label || null,
      is_previous_stage:      false,
    });
    if (resp.error) { toast(resp.error.message || "Failed to add rejection rule.", "error"); return; }
    toast("Rejection rule added.", "success");
    fetchRejectionRules(stageId);
  };

  const removeRejectionRule = async (ruleId, stageId) => {
    const resp = await deleteData(`t2t/workflows/${wfId}/rejection-rules/${ruleId}`);
    if (resp.error) { toast(resp.error.message || "Failed to remove rule.", "error"); return; }
    fetchRejectionRules(stageId);
  };

  const addSubStageAndDrill = async (parentStageId, parentStageName) => {
    if (!wfId || wfId === "new") return;
    const payload = {
      name:             "New Sub-stage",
      stage_type:       "approval",
      x:                80,
      y:                100,
      role_ids:         [],
      user_ids:         [],
      sla_hours:        null,
      parent_stage_id:  parentStageId,
    };
    const resp = await postData(`t2t/workflows/${wfId}/stages`, payload);
    if (resp.error) { toast(resp.error.message || "Failed to create sub-stage.", "error"); return; }
    const raw = resp.data?.data;
    const s   = raw?.stage_id ? raw : (raw?.data || raw);
    setNodes((prev) => [...prev, {
      id:                    s.stage_id,
      type:                  s.stage_type || "approval",
      label:                 s.name,
      x:                     s.x ?? 80,
      y:                     s.y ?? 100,
      roles:                 [],
      sla:                   "",
      color:                 getStageColor(s.stage_type || "approval"),
      escalation_l1_before_mins: "", escalation_l1_before_to: { roles: [], users: [] },
      escalation_l1_after_mins:  "", escalation_l1_after_to:  { roles: [], users: [] },
      escalation_l2_before_mins: "", escalation_l2_before_to: { roles: [], users: [] },
      escalation_l2_after_mins:  "", escalation_l2_after_to:  { roles: [], users: [] },
      escalation_l3_before_mins: "", escalation_l3_before_to: { roles: [], users: [] },
      escalation_l3_after_mins:  "", escalation_l3_after_to:  { roles: [], users: [] },
      completion_rule:       "all",
      parentId:              parentStageId,
    }]);
    drillIntoStage(parentStageId, parentStageName);
  };

  // BUG-37 — toggle a specific user on/off for the current stage
  const toggleUser = (userId) => {
    setPropsForm((p) => {
      const users = p.users || [];
      return {
        ...p,
        users: users.includes(userId) ? users.filter((u) => u !== userId) : [...users, userId],
      };
    });
  };

  const toggleRole = (roleId) => {
    setPropsForm((p) => {
      const roles = p.roles || [];
      return {
        ...p,
        roles: roles.includes(roleId) ? roles.filter((r) => r !== roleId) : [...roles, roleId],
      };
    });
  };

  // ---- Drill-down filtering ----
  const currentParentId  = stageStack.length > 0 ? stageStack[stageStack.length - 1].id : null;
  const displayedNodes   = nodes.filter((n) => (n.parentId ?? null) === currentParentId);
  const displayedNodeIds = new Set(displayedNodes.map((n) => n.id));
  const displayedConns   = conns.filter((c) => displayedNodeIds.has(c.from) && displayedNodeIds.has(c.to));
  const subStageCount    = (nodeId) => nodes.filter((n) => n.parentId === nodeId).length;

  const selectedNode = selected?.type === "node" ? nodes.find((n) => n.id === selected.id) : null;
  const selectedConn = selected?.type === "conn" ? conns.find((c) => c.id === selected.id) : null;

  if (pageLoading) {
    return (
      <>
        <T2TTopNav title="" />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100vh - 52px)", fontSize: 13, color: "var(--t2t-text-muted)" }}>
          Loading workflow...
        </div>
      </>
    );
  }

  if (pageError) {
    return (
      <>
        <T2TTopNav title="" />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "calc(100vh - 52px)", gap: 12 }}>
          <div style={{ color: "var(--t2t-danger)", fontSize: 13 }}>{pageError}</div>
          <button className="t2t-btn t2t-btn-ghost" onClick={() => navigate("/t2t-home")}>← Back to Dashboard</button>
        </div>
      </>
    );
  }

  return (
    <>
      <T2TTopNav title="" />
      <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 52px)", background: "var(--t2t-bg)" }}>

        {/* Builder toolbar */}
        <div className="t2t-builder-bar">
          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => navigate("/t2t-home")}>← Back</button>
          <div style={{ position: "relative" }}>
            <input
              className="t2t-builder-wf-name"
              value={wfName}
              onChange={(e) => { setWfName(e.target.value); if (wfNameConflict) setWfNameConflict(""); }}
              onBlur={handleWfNameBlur}
              style={{ borderColor: wfNameConflict ? "#DC2626" : undefined }}
            />
            {wfNameConflict && (
              <div style={{ position: "absolute", top: "100%", left: 0, fontSize: 11, color: "#DC2626", fontWeight: 600, whiteSpace: "nowrap", marginTop: 2 }}>
                {wfNameConflict}
              </div>
            )}
          </div>
          <div className="t2t-tab-group">
            <button className={`t2t-tab${activeTab === "canvas" ? " active" : ""}`} onClick={() => setActiveTab("canvas")}>Canvas</button>
            <button className={`t2t-tab${activeTab === "form"   ? " active" : ""}`} onClick={() => setActiveTab("form")}>Form Fields</button>
          </div>

          {/* fix #2b-i: Request Types multi-picker */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }} title="Attach one or more Request Types to define the form schema. Leave empty to author fields per-workflow.">
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--t2t-text-muted)" }}>Request Types:</span>
            <RequestTypeMultiPicker
              requestTypes={requestTypes}
              selectedIds={attachedRequestTypeIds}
              onChange={changeRequestTypeIds}
              onSave={saveRequestTypes}
              disabled={saving || wfStatus === "active"}
            />
          </div>
          <div className="t2t-btn-group" style={{ marginLeft: "auto" }}>
            {wfStatus === "active" && (
              <span style={{ fontSize: 11, fontWeight: 700, color: "#16A34A", padding: "2px 8px", background: "#F0FDF4", borderRadius: 20, border: "1px solid #BBF7D0" }}>✓ Published</span>
            )}
            {(() => {
              const issues = runHealthCheck(nodes, conns);
              const critical = issues.filter((i) => i.severity === "critical").length;
              const warnings = issues.filter((i) => i.severity === "warning").length;
              const icon  = critical > 0 ? "🔴" : warnings > 0 ? "🟡" : nodes.length > 0 ? "🟢" : "⚪";
              const label = critical > 0 ? `${critical} issue${critical > 1 ? "s" : ""}` : warnings > 0 ? `${warnings} warning${warnings > 1 ? "s" : ""}` : nodes.length > 0 ? "Healthy" : "Empty";
              return (
                <span
                  title="Workflow health"
                  style={{ fontSize: 11, fontWeight: 600, color: "#475569", padding: "2px 8px", background: "#F8FAFC", borderRadius: 20, border: "1px solid #E2E8F0", cursor: "pointer", userSelect: "none" }}
                  onClick={() => { const i = runHealthCheck(nodes, conns); if (i.length > 0) setHealthModal({ open: true, issues: i, proceedToPublish: !i.some((x) => x.severity === "critical") }); }}
                >
                  {icon} {label}
                </span>
              );
            })()}
            <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={fitCanvas} title="Fit all stages into view">⊞ Fit</button>
            <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={openHistoryModal}>📋 History</button>
            <Hint id="conditions.preview" position="bottom">
              <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={openTestModal}>▶ Test</button>
            </Hint>
            {hasPermission("workflow_builder.publish") && (
              <Hint id="publish.action" position="bottom">
                <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={openPublishModal}>✓ Publish</button>
              </Hint>
            )}
            <button type="button" className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setHelpOpen(true); }} title="Help & Guidance">? Help</button>
          </div>
        </div>

        {/* CANVAS TAB */}
        {activeTab === "canvas" && (
          <div className="t2t-builder-body" style={{ flex: 1, overflow: "hidden" }}>

            {/* Palette */}
            <div className="t2t-palette">
              <Hint id="canvas.add_stage" position="right">
                <div className="t2t-pal-section">Stage Types</div>
              </Hint>
              {STAGE_TYPES.map((s) => (
                <div
                  key={s.type}
                  className="t2t-pal-item"
                  draggable
                  onDragStart={() => { palDragType.current = s.type; }}
                  onDragEnd={() => { palDragType.current = null; }}
                >
                  <div className="t2t-pal-dot" style={{ background: s.color }} />
                  <div>
                    <div className="t2t-pal-name">{s.label}</div>
                    <div className="t2t-pal-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
              <div className="t2t-pal-divider" />
              <div className="t2t-pal-section">Tips</div>
              <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", lineHeight: 1.7, padding: "0 4px" }}>
                Drag stages onto canvas. Click the <strong>▶</strong> port to draw a connection. Press <strong>Esc</strong> to cancel.
              </div>
            </div>

            {/* Breadcrumb */}
            <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
            {stageStack.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", background: "#EFF6FF", borderBottom: "1px solid #BFDBFE", fontSize: 12, flexShrink: 0 }}>
                <span
                  style={{ color: "var(--t2t-primary)", cursor: "pointer", fontWeight: 600 }}
                  onClick={() => drillUpTo(0)}
                >
                  {wfName}
                </span>
                {stageStack.map((s, i) => (
                  <React.Fragment key={s.id}>
                    <span style={{ color: "#94A3B8" }}>›</span>
                    <span
                      style={{ color: i === stageStack.length - 1 ? "var(--t2t-text-secondary)" : "var(--t2t-primary)", cursor: i < stageStack.length - 1 ? "pointer" : "default", fontWeight: 600 }}
                      onClick={() => i < stageStack.length - 1 && drillUpTo(i + 1)}
                    >
                      {s.name}
                    </span>
                  </React.Fragment>
                ))}
                <span style={{ marginLeft: "auto", fontSize: 11, color: "#64748B" }}>Sub-canvas — drag stages here</span>
              </div>
            )}

            {/* Canvas */}
            <div
              ref={canvasRef}
              className={`t2t-canvas-wrap${connecting !== null ? " connecting-mode" : ""}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleCanvasDrop}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onClick={handleCanvasClick}
            >
              <div id="t2t-canvas-inner" className="t2t-canvas-inner">

                {/* Canvas welcome hint — shows when empty */}
                {displayedNodes.length === 0 && (
                  <div style={{ position: "absolute", top: 80, left: "50%", transform: "translateX(-50%)", zIndex: 10 }}>
                    <Hint id="canvas.welcome" position="bottom">
                      <div style={{ textAlign: "center", padding: "20px 30px", color: "var(--t2t-text-muted)", fontSize: 13 }}>
                        Drag a stage from the palette on the left to get started.
                      </div>
                    </Hint>
                  </div>
                )}

                {/* SVG arrows */}
                <svg className="t2t-canvas-svg">
                  <defs>
                    <marker id="t2t-arr"        markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                      <polygon points="0 0,8 3,0 6" fill="#94A3B8" />
                    </marker>
                    <marker id="t2t-arr-green"  markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                      <polygon points="0 0,8 3,0 6" fill="#36B82A" />
                    </marker>
                    <marker id="t2t-arr-purple" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                      <polygon points="0 0,8 3,0 6" fill="#7C3AED" />
                    </marker>
                  </defs>

                  {displayedConns.map((c) => {
                    const isSelected  = selected?.type === "conn" && selected.id === c.id;
                    const hasCond     = c.conditions?.rules?.length > 0;
                    const fromNode = displayedNodes.find((n) => n.id === c.from);
                    const toNode   = displayedNodes.find((n) => n.id === c.to);
                    if (!fromNode || !toNode) return null;
                    const h1   = nodeHeightsRef.current[c.from] || 88;
                    const h2   = nodeHeightsRef.current[c.to]   || 88;
                    const from = { x: fromNode.x + NODE_W, y: fromNode.y + h1 / 2 };
                    const to   = { x: toNode.x,            y: toNode.y   + h2 / 2 };
                    const path = makePath(from.x, from.y, to.x, to.y);
                    const mx   = (from.x + to.x) / 2;
                    const my   = (from.y + to.y) / 2 - 10;
                    const stroke      = isSelected ? "#36B82A" : hasCond ? "#7C3AED" : "#94A3B8";
                    const markerEnd   = isSelected ? "url(#t2t-arr-green)" : hasCond ? "url(#t2t-arr-purple)" : "url(#t2t-arr)";
                    const dashArray   = hasCond && !isSelected ? "6 4" : undefined;
                    return (
                      <g
                        key={c.id}
                        onClick={(e) => { e.stopPropagation(); setSelected({ type: "conn", id: c.id }); setPropsForm({ label: c.label, action: c.action || "approve" }); setPropsOpen(true); }}
                        style={{ cursor: "pointer" }}
                      >
                        <path d={path} fill="none" stroke="transparent" strokeWidth={12} style={{ pointerEvents: "stroke" }} />
                        <path d={path} fill="none"
                          stroke={stroke}
                          strokeWidth={isSelected ? 2.5 : 2}
                          strokeDasharray={dashArray}
                          markerEnd={markerEnd}
                        />
                        {/* IF badge + condition preview for conditional transitions */}
                        {hasCond && !isSelected && (() => {
                          const preview = buildCondPreview(c.conditions);
                          const short   = preview.length > 30 ? preview.slice(0, 28) + "…" : preview;
                          const pw      = Math.max(short.length * 6.5 + 16, 50);
                          return (
                            <g>
                              <rect x={mx - 12} y={my - 13} width={24} height={14} rx={7} fill="#7C3AED" />
                              <text x={mx} y={my - 3} textAnchor="middle" fontSize={9} fill="white" fontWeight={700}>IF</text>
                              {short && (
                                <>
                                  <rect x={mx - pw / 2} y={my + 4} width={pw} height={15} rx={4} fill="white" stroke="#7C3AED" strokeWidth={0.8} />
                                  <text x={mx} y={my + 14} textAnchor="middle" fontSize={9} fill="#7C3AED" fontWeight={600}>{short}</text>
                                </>
                              )}
                            </g>
                          );
                        })()}
                        {c.label && (
                          <text x={mx} y={my + (hasCond ? 26 : 0)} textAnchor="middle" fontSize={11} fill={hasCond ? "#7C3AED" : "#475569"} fontWeight={600}>{c.label}</text>
                        )}
                      </g>
                    );
                  })}

                  {connLine && (
                    <path d={connLine} fill="none" stroke="#36B82A" strokeWidth={2} strokeDasharray="8 4" opacity="0.7" />
                  )}
                </svg>

                {/* Stage nodes */}
                {/* Compute duplicate names at this level for R3 */}
                {(() => {
                  const labelCounts = {};
                  displayedNodes.forEach((n) => {
                    const key = n.label?.trim().toLowerCase();
                    if (key) labelCounts[key] = (labelCounts[key] || 0) + 1;
                  });
                  return displayedNodes.map((node) => {
                  const isSelected       = selected?.type === "node" && selected.id === node.id;
                  const isConnectingFrom = connecting === node.id;
                  const isDuplicateName  = labelCounts[node.label?.trim().toLowerCase()] > 1;
                  return (
                    <div
                      key={node.id}
                      id={`t2t-node-${node.id}`}
                      className={`t2t-snode${isSelected ? " selected" : ""}`}
                      style={{ left: node.x, top: node.y, outline: isDuplicateName ? "2px solid #DC2626" : undefined }}
                      title={isDuplicateName ? "Duplicate stage name — rename this stage" : undefined}
                    >
                      <div
                        className="t2t-snode-head"
                        onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                        onDoubleClick={(e) => { e.stopPropagation(); openNodeProps(node.id); }}
                        title="Double-click to edit properties"
                      >
                        <div className="t2t-snode-dot" style={{ background: node.color }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="t2t-snode-name">{node.label}</div>
                          <div className="t2t-snode-type">{node.type}</div>
                        </div>
                      </div>
                      <div className="t2t-snode-body">
                        {node.roles?.length > 0 ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 5 }}>
                            {node.roles.map((roleId) => {
                              const role = availableRoles.find((ar) => ar.role_id === roleId);
                              return (
                                <span key={roleId} style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600, color: "white", background: role?.color || node.color }}>
                                  {role?.name || roleId}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", marginBottom: 5 }}>No role assigned</div>
                        )}
                        <div style={{ fontSize: 10, color: "var(--t2t-text-muted)" }}>
                          ⏱ {node.sla ? `${node.sla}h SLA` : "No SLA"}
                        </div>
                        {subStageCount(node.id) > 0 && (
                          <div
                            style={{ marginTop: 5, display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600, background: "#DBEAFE", color: "#1D4ED8", border: "1px solid #BFDBFE", cursor: "pointer" }}
                            onClick={(e) => { e.stopPropagation(); drillIntoStage(node.id, node.label); }}
                          >
                            ➙ Sub-stages ({subStageCount(node.id)})
                          </div>
                        )}
                      </div>
                      <div className="t2t-snode-btns">
                        <button className="t2t-snode-btn del" title="Delete" onClick={(e) => { e.stopPropagation(); deleteNode(node.id); }}>✕</button>
                      </div>
                      {/* Port In */}
                      <div
                        className={`t2t-port t2t-port-in${connecting !== null && !isConnectingFrom ? " hot" : ""}`}
                        onClick={(e) => handlePortInClick(e, node.id)}
                      />
                      {/* Port Out */}
                      {node.type !== "end" && (
                        <div className="t2t-port t2t-port-out" title="Click to connect →" onClick={(e) => handlePortOutClick(e, node.id)}>▶</div>
                      )}
                    </div>
                  );
                  });
                })()}

                {displayedNodes.length === 0 && !pageLoading && (
                  <div className="t2t-canvas-hint">
                    {stageStack.length > 0
                      ? "Drag stage types from the left panel to add sub-stages here"
                      : "Drag stage types from the left panel • Click ▶ port to connect stages"}
                  </div>
                )}
              </div>
            </div>
            </div>{/* end breadcrumb wrapper */}

            {/* Properties Panel — node requires double-click; connection opens on single click */}
            {(selected?.type === "conn" || (selected?.type === "node" && propsOpen)) && (
              <div ref={propsPanelRef} className="t2t-props-panel" style={{ display: "block" }}>
                <div className="t2t-props-head">
                  <span className="t2t-props-title">
                    {selected.type === "node" ? "Stage Properties" : "Connection Properties"}
                  </span>
                  <button className="t2t-close-btn" onClick={() => { setSelected(null); setPropsOpen(false); }}>×</button>
                </div>

                {/* Node props */}
                {selected.type === "node" && selectedNode && (
                  <>
                    {/* BASIC */}
                    <div className="t2t-props-sec">
                      <div className="t2t-props-sec-title">Basic</div>
                      <div className="t2t-fg">
                        <label>Stage Name</label>
                        <input className="t2t-fc" value={propsForm.label || ""} onChange={(e) => setPropsForm((p) => ({ ...p, label: e.target.value }))} />
                      </div>
                      <div className="t2t-fg" style={{ marginBottom: 0 }}>
                        <label>Stage Type</label>
                        <select className="t2t-fc" value={propsForm.stage_type || ""} onChange={(e) => setPropsForm((p) => ({ ...p, stage_type: e.target.value }))}>
                          {STAGE_TYPES.map((s) => <option key={s.type} value={s.type}>{s.label}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* ARCH-05a: SYSTEM ACTION — only visible for stage_type=system_action */}
                    {propsForm.stage_type === "system_action" && (
                      <div className="t2t-props-sec">
                        <div className="t2t-props-sec-title">System Action</div>

                        <div className="t2t-fg">
                          <label>Action Type *</label>
                          <select className="t2t-fc"
                            value={propsForm.system_action_type || ""}
                            onChange={(e) => setPropsForm((p) => ({
                              ...p,
                              system_action_type:   e.target.value,
                              system_action_config: {}, // clear config when subtype changes
                            }))}>
                            <option value="">Select an action...</option>
                            {SYSTEM_ACTION_SUBTYPES.map((s) => (
                              <option key={s.value} value={s.value}>{s.icon} {s.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Validator-family subtypes: single form-field picker + block flag */}
                        {(propsForm.system_action_type === "pan_validate" ||
                          propsForm.system_action_type === "gst_validate") && (
                          <>
                            <div className="t2t-fg">
                              <label>Source Form Field</label>
                              <select className="t2t-fc"
                                value={propsForm.system_action_config?.source_field || ""}
                                onChange={(e) => setPropsForm((p) => ({
                                  ...p,
                                  system_action_config: { ...(p.system_action_config || {}), source_field: e.target.value },
                                }))}>
                                <option value="">— pick a field —</option>
                                {formFields.map((f) => (
                                  <option key={f.id} value={f.field_id || f.id}>
                                    {f.label} ({f.field_id || f.id})
                                  </option>
                                ))}
                              </select>
                              {formFields.length === 0 && (
                                <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginTop: 4 }}>
                                  No form fields available. Attach a Request Type to the workflow or define fields in the Form Fields tab.
                                </div>
                              )}
                            </div>
                            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginBottom: 8, cursor: "pointer" }}>
                              <input type="checkbox"
                                checked={!!propsForm.system_action_config?.block_on_fail}
                                onChange={(e) => setPropsForm((p) => ({
                                  ...p,
                                  system_action_config: { ...(p.system_action_config || {}), block_on_fail: e.target.checked },
                                }))} />
                              Block workflow if validation fails
                            </label>
                          </>
                        )}

                        {/* Duplicate check: multi-field picker */}
                        {propsForm.system_action_type === "duplicate_check" && (
                          <div className="t2t-fg">
                            <label>Fields to check for duplicates</label>
                            {formFields.length === 0 ? (
                              <div style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>
                                No form fields available. Attach a Request Type first.
                              </div>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                {formFields.map((f) => {
                                  const key = f.field_id || f.id;
                                  const checked = (propsForm.system_action_config?.check_fields || []).includes(key);
                                  return (
                                    <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                                      <input type="checkbox" checked={checked}
                                        onChange={() => setPropsForm((p) => {
                                          const cur = p.system_action_config?.check_fields || [];
                                          const next = checked ? cur.filter((k) => k !== key) : [...cur, key];
                                          return { ...p, system_action_config: { ...(p.system_action_config || {}), check_fields: next } };
                                        })} />
                                      {f.label}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* 3-way match: three specific field pickers */}
                        {propsForm.system_action_type === "three_way_match" && (
                          <>
                            {["po_field", "grn_field", "invoice_field"].map((key) => (
                              <div className="t2t-fg" key={key}>
                                <label>{key.replace("_", " ").toUpperCase()}</label>
                                <select className="t2t-fc"
                                  value={propsForm.system_action_config?.[key] || ""}
                                  onChange={(e) => setPropsForm((p) => ({
                                    ...p,
                                    system_action_config: { ...(p.system_action_config || {}), [key]: e.target.value },
                                  }))}>
                                  <option value="">— pick a field —</option>
                                  {formFields.map((f) => (
                                    <option key={f.id} value={f.field_id || f.id}>{f.label}</option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </>
                        )}

                        {/* Push-family subtypes (SAP / Ariba / Fiori): operation name + key→value mapping */}
                        {(propsForm.system_action_type === "sap_sync" ||
                          propsForm.system_action_type === "ariba_push" ||
                          propsForm.system_action_type === "fiori_sync") && (
                          <>
                            <div className="t2t-fg">
                              <label>Operation</label>
                              <input className="t2t-fc"
                                placeholder="e.g. create_vendor, create_po"
                                value={propsForm.system_action_config?.operation || ""}
                                onChange={(e) => setPropsForm((p) => ({
                                  ...p,
                                  system_action_config: { ...(p.system_action_config || {}), operation: e.target.value },
                                }))} />
                            </div>
                            {/* BUG-39: row editor for Field Mapping. Replaces the textarea
                                that silently dropped empty lines + auto-appended "=" on every
                                keystroke. Lives as a subcomponent so it can hold its own
                                array state for empty-row buffering. Backend contract
                                unchanged — state is converted to {key:val} object on save. */}
                            <div className="t2t-fg">
                              <label>Field Mapping <span style={{ fontWeight: 400, color: "var(--t2t-text-muted)" }}>(form field → external field)</span></label>
                              <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginBottom: 8, lineHeight: 1.4 }}>
                                Map values from this request type&apos;s form into the external system&apos;s field names.
                                The left side is a form field key (e.g. <code>vendor_name</code>); the right is the SAP/Ariba/Fiori target (e.g. <code>LFA1-NAME1</code>).
                              </div>
                              <FieldMappingEditor
                                mapping={propsForm.system_action_config?.mapping || {}}
                                formFields={formFields}
                                onChange={(nextMappingObj) => setPropsForm((p) => ({
                                  ...p,
                                  system_action_config: { ...(p.system_action_config || {}), mapping: nextMappingObj },
                                }))}
                              />
                            </div>
                          </>
                        )}

                        {/* Common: on_success / on_failure — stored now, used by 05b runtime executor */}
                        {propsForm.system_action_type && (
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <div className="t2t-fg">
                              <label>On success</label>
                              <select className="t2t-fc"
                                value={propsForm.system_action_config?.on_success || "advance"}
                                onChange={(e) => setPropsForm((p) => ({
                                  ...p,
                                  system_action_config: { ...(p.system_action_config || {}), on_success: e.target.value },
                                }))}>
                                <option value="advance">Advance</option>
                                <option value="block">Block (human review)</option>
                              </select>
                            </div>
                            <div className="t2t-fg">
                              <label>On failure</label>
                              <select className="t2t-fc"
                                value={propsForm.system_action_config?.on_failure || "block"}
                                onChange={(e) => setPropsForm((p) => ({
                                  ...p,
                                  system_action_config: { ...(p.system_action_config || {}), on_failure: e.target.value },
                                }))}>
                                <option value="block">Block (human review)</option>
                                <option value="advance">Advance anyway</option>
                              </select>
                            </div>
                          </div>
                        )}

                        <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginTop: 6, padding: "6px 8px", background: "#F1F5F9", borderRadius: 4 }}>
                          ⓘ This stage executes automatically when a request enters it. Outcome branches via on_success / on_failure. Failed attempts are retried up to 3× (5-min intervals).
                        </div>
                      </div>
                    )}

                    {/* ASSIGNED ROLES */}
                    <div className="t2t-props-sec">
                      <Hint id="roles.intro" position="left">
                        <div className="t2t-props-sec-title">Assigned Roles</div>
                      </Hint>
                      {availableRoles.length === 0 && (
                        <div style={{ fontSize: 12, color: "var(--t2t-text-muted)" }}>No roles found.</div>
                      )}
                      {availableRoles.length > 0 && (() => {
                        // BUG-36: Select all / Clear strip + counter + hint
                        const selectedCount = (propsForm.roles || []).filter((id) =>
                          availableRoles.some((r) => r.role_id === id)
                        ).length;
                        const total = availableRoles.length;
                        const allSelected = selectedCount === total;
                        return (
                          <>
                            <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginBottom: 6, lineHeight: 1.4 }}>
                              Multi-select — anyone holding any selected role can act on this stage.
                            </div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 6 }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--t2t-text-secondary)" }}>
                                {selectedCount} of {total} role{total !== 1 ? "s" : ""} selected
                              </span>
                              <div style={{ display: "flex", gap: 4 }}>
                                <button
                                  type="button"
                                  onClick={() => setPropsForm((p) => ({
                                    ...p,
                                    roles: allSelected ? [] : availableRoles.map((r) => r.role_id),
                                  }))}
                                  style={{
                                    background: "transparent", border: "1px solid var(--t2t-border)",
                                    borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                                    cursor: "pointer", color: "var(--t2t-text-secondary)",
                                  }}
                                  title={allSelected ? "Clear all selected roles" : "Select every role"}
                                >
                                  {allSelected ? "✕ Clear" : "✓ Select all"}
                                </button>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                      {availableRoles.map((role) => {
                        const isSelected = (propsForm.roles || []).includes(role.role_id);
                        return (
                          <div
                            key={role.role_id}
                            onClick={() => toggleRole(role.role_id)}
                            style={{
                              display: "flex", alignItems: "center", gap: 10,
                              padding: "8px 10px", borderRadius: 6, cursor: "pointer", marginBottom: 4,
                              border: `1px solid ${isSelected ? role.color || "var(--t2t-primary)" : "var(--t2t-border)"}`,
                              background: isSelected ? `${role.color}18` : "transparent",
                            }}
                          >
                            <div style={{ width: 10, height: 10, borderRadius: "50%", background: role.color || "#94A3B8", flexShrink: 0 }} />
                            <span style={{ flex: 1, fontSize: 13, color: "var(--t2t-text-secondary)" }}>{role.name}</span>
                            {isSelected && <span style={{ fontSize: 12, color: role.color || "var(--t2t-primary)", fontWeight: 700 }}>✓</span>}
                          </div>
                        );
                      })}
                    </div>

                    {/* SPECIFIC USERS — BUG-37 */}
                    <SpecificUsersSection
                      availableUsers={availableUsers}
                      availableRoles={availableRoles}
                      selectedUserIds={propsForm.users || []}
                      onToggleUser={toggleUser}
                      onReplaceUsers={(ids) => setPropsForm((p) => ({ ...p, users: ids }))}
                    />

                    {/* SLA & ESCALATION */}
                    <div className="t2t-props-sec">
                      <div className="t2t-props-sec-title">SLA &amp; Escalation</div>
                      <div className="t2t-fg">
                        <label>SLA Hours <span style={{ fontWeight: 400, color: "var(--t2t-text-muted)" }}>(leave blank for none)</span></label>
                        <input className="t2t-fc" type="number" min="0" placeholder="e.g. 2" value={propsForm.sla || ""} onChange={(e) => setPropsForm((p) => ({ ...p, sla: e.target.value }))} />
                      </div>

                      {[1, 2, 3].map((lvl) => {
                        const bTo   = propsForm[`escalation_l${lvl}_before_to`]   || { roles: [], users: [] };
                        const aTo   = propsForm[`escalation_l${lvl}_after_to`]    || { roles: [], users: [] };
                        const normTo = (v) => {
                          if (v && typeof v === "object" && !Array.isArray(v)) return { roles: Array.isArray(v.roles) ? v.roles : [], users: Array.isArray(v.users) ? v.users : [] };
                          if (v && typeof v === "string") return { roles: [v], users: [] };
                          if (v && typeof v === "number") return { roles: [v], users: [] };
                          return { roles: [], users: [] };
                        };
                        const bToObj = normTo(bTo);
                        const aToObj = normTo(aTo);

                        const toggleRole = (key, roleId) => {
                          setPropsForm((p) => {
                            const cur = normTo(p[key]);
                            const has = cur.roles.includes(roleId);
                            const newRoles = has ? cur.roles.filter((r) => r !== roleId) : [...cur.roles, roleId];
                            const validUsers = has ? cur.users.filter((uid) => {
                              const u = availableUsers.find((au) => String(au.user_id) === String(uid));
                              return u && newRoles.includes(u.t2t_role_id);
                            }) : cur.users;
                            return { ...p, [key]: { roles: newRoles, users: validUsers } };
                          });
                        };
                        const toggleUser = (key, userId) => {
                          setPropsForm((p) => {
                            const cur = normTo(p[key]);
                            const has = cur.users.includes(userId);
                            return { ...p, [key]: { ...cur, users: has ? cur.users.filter((u) => u !== userId) : [...cur.users, userId] } };
                          });
                        };

                        const renderNotifyPicker = (label, minsKey, toKey, toObj, placeholder) => (
                          <>
                            <div className="t2t-fg">
                              <label>{label}</label>
                              <input className="t2t-fc" type="number" min="0" placeholder={placeholder}
                                value={propsForm[minsKey] || ""}
                                onChange={(e) => setPropsForm((p) => ({ ...p, [minsKey]: e.target.value }))} />
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: "#64748B", marginBottom: 4 }}>Notify Roles:</div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {availableRoles.map((r) => {
                                  const sel = toObj.roles.includes(r.role_id);
                                  return (
                                    <span key={r.role_id} onClick={() => toggleRole(toKey, r.role_id)}
                                      style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600, cursor: "pointer",
                                        color: sel ? "white" : "#475569", background: sel ? (r.color || "#36B82A") : "#F1F5F9",
                                        border: sel ? "none" : "1px solid #E2E8F0" }}>
                                      {sel && "✓ "}{r.name}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                            {toObj.roles.length > 0 && (() => {
                              const scopedUsers = availableUsers.filter((u) => {
                                if (wfGroupId && u.t2t_group_id && String(u.t2t_group_id) !== String(wfGroupId)) return false;
                                if (wfCompanyId && u.t2t_company_id && String(u.t2t_company_id) !== String(wfCompanyId)) return false;
                                return true;
                              });
                              const usersInRoles = scopedUsers.filter((u) => toObj.roles.includes(u.t2t_role_id));
                              if (usersInRoles.length === 0) return null;
                              const selectedCount = toObj.users.length;
                              const dropdownKey = `${toKey}_open`;
                              const searchKey = `${toKey}_search`;
                              const isOpen = propsForm[dropdownKey] || false;
                              const searchTerm = (propsForm[searchKey] || "").toLowerCase();
                              return (
                                <div style={{ marginBottom: 8, position: "relative" }}>
                                  <div style={{ fontSize: 10, fontWeight: 600, color: "#64748B", marginBottom: 4 }}>
                                    Specific Users <span style={{ fontWeight: 400 }}>({selectedCount === 0 ? "all in selected roles" : `${selectedCount} selected`})</span>
                                  </div>
                                  {/* Toggle button */}
                                  <div
                                    className="t2t-fc"
                                    onClick={() => setPropsForm((p) => ({ ...p, [dropdownKey]: !p[dropdownKey] }))}
                                    style={{ cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 32 }}
                                  >
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {selectedCount === 0
                                        ? "All users (click to pick specific)"
                                        : toObj.users.map((uid) => { const u = availableUsers.find((au) => au.user_id === uid); return u?.email || u?.name || uid; }).join(", ")
                                      }
                                    </span>
                                    <span style={{ fontSize: 8, marginLeft: 4 }}>{isOpen ? "▲" : "▼"}</span>
                                  </div>
                                  {/* Dropdown panel */}
                                  {isOpen && (
                                    <div style={{ position: "absolute", zIndex: 100, top: "100%", left: 0, right: 0, background: "white", border: "1px solid #E2E8F0", borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", maxHeight: 220, display: "flex", flexDirection: "column" }}>
                                      {/* Search */}
                                      <div style={{ padding: "6px 8px", borderBottom: "1px solid #F1F5F9" }}>
                                        <input
                                          className="t2t-fc"
                                          type="text"
                                          placeholder="Search by email or name..."
                                          value={propsForm[searchKey] || ""}
                                          onChange={(e) => setPropsForm((p) => ({ ...p, [searchKey]: e.target.value }))}
                                          onClick={(e) => e.stopPropagation()}
                                          style={{ fontSize: 11, margin: 0 }}
                                          autoFocus
                                        />
                                      </div>
                                      {/* Select All / Clear */}
                                      <div style={{ display: "flex", gap: 8, padding: "4px 8px", borderBottom: "1px solid #F1F5F9", fontSize: 10 }}>
                                        <span
                                          style={{ color: "var(--t2t-primary)", cursor: "pointer", fontWeight: 600 }}
                                          onClick={() => {
                                            const allIds = usersInRoles.map((u) => u.user_id);
                                            setPropsForm((p) => ({ ...p, [toKey]: { ...normTo(p[toKey]), users: allIds } }));
                                          }}
                                        >Select All</span>
                                        <span
                                          style={{ color: "#DC2626", cursor: "pointer", fontWeight: 600 }}
                                          onClick={() => setPropsForm((p) => ({ ...p, [toKey]: { ...normTo(p[toKey]), users: [] } }))}
                                        >Clear</span>
                                      </div>
                                      {/* User list */}
                                      <div style={{ overflowY: "auto", flex: 1 }}>
                                        {toObj.roles.map((roleId) => {
                                          const role = availableRoles.find((ar) => ar.role_id === roleId);
                                          const roleUsers = scopedUsers.filter((u) => u.t2t_role_id === roleId);
                                          const filtered = searchTerm
                                            ? roleUsers.filter((u) => (u.email || "").toLowerCase().includes(searchTerm) || (u.name || "").toLowerCase().includes(searchTerm))
                                            : roleUsers;
                                          if (filtered.length === 0) return null;
                                          return (
                                            <div key={roleId}>
                                              <div style={{ fontSize: 9, fontWeight: 700, color: role?.color || "#475569", padding: "4px 10px 2px", background: "#F8FAFC" }}>{role?.name}</div>
                                              {filtered.map((u) => {
                                                const sel = toObj.users.includes(u.user_id);
                                                return (
                                                  <label
                                                    key={u.user_id}
                                                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", cursor: "pointer", fontSize: 11, background: sel ? "#EFF6FF" : "transparent" }}
                                                    onClick={(e) => e.stopPropagation()}
                                                  >
                                                    <input type="checkbox" checked={sel} onChange={() => toggleUser(toKey, u.user_id)} style={{ width: 13, height: 13, flexShrink: 0 }} />
                                                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email || u.name}</span>
                                                    {u.name && u.email && <span style={{ fontSize: 9, color: "#94A3B8", flexShrink: 0 }}>{u.name}</span>}
                                                  </label>
                                                );
                                              })}
                                            </div>
                                          );
                                        })}
                                      </div>
                                      {/* Done button */}
                                      <div style={{ padding: "4px 8px", borderTop: "1px solid #F1F5F9", textAlign: "right" }}>
                                        <button
                                          className="t2t-btn t2t-btn-primary t2t-btn-sm"
                                          style={{ fontSize: 10, padding: "2px 12px" }}
                                          onClick={() => setPropsForm((p) => ({ ...p, [dropdownKey]: false, [searchKey]: "" }))}
                                        >Done</button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        );

                        return (
                          <div key={lvl} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: "10px 12px", marginBottom: lvl < 3 ? 10 : 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 8 }}>Escalation Level {lvl}</div>
                            {renderNotifyPicker(`Before SLA breach (mins)`, `escalation_l${lvl}_before_mins`, `escalation_l${lvl}_before_to`, bToObj, lvl === 1 ? "e.g. 30" : lvl === 2 ? "e.g. 60" : "e.g. 90")}
                            <div style={{ borderTop: "1px dashed #E2E8F0", margin: "8px 0" }} />
                            {renderNotifyPicker(`After SLA breach (mins)`, `escalation_l${lvl}_after_mins`, `escalation_l${lvl}_after_to`, aToObj, lvl === 1 ? "e.g. 15" : lvl === 2 ? "e.g. 30" : "e.g. 60")}
                          </div>
                        );
                      })}
                    </div>

                    {/* Parallel Completion Rule */}
                    {propsForm.stage_type === "parallel" && (
                      <div className="t2t-props-sec">
                        <div className="t2t-props-sec-title">Parallel Completion Rule</div>
                        <div className="t2t-fg" style={{ marginBottom: 0 }}>
                          <label>Proceed when</label>
                          <select className="t2t-fc" value={propsForm.completion_rule || "all"} onChange={(e) => setPropsForm((p) => ({ ...p, completion_rule: e.target.value }))}>
                            <option value="all">All sub-stages complete</option>
                            <option value="any">Any one sub-stage completes</option>
                            <option value="majority">Majority complete</option>
                            <option value="custom_n">Custom N complete</option>
                          </select>
                          {propsForm.completion_rule === "custom_n" && (
                            <div style={{ marginTop: 8 }}>
                              <label style={{ fontSize: 12, color: "var(--t2t-text-secondary)" }}>Minimum sub-stages required</label>
                              <input
                                className="t2t-fc"
                                type="number"
                                min="1"
                                placeholder="e.g. 2"
                                value={propsForm.completion_n || ""}
                                onChange={(e) => setPropsForm((p) => ({ ...p, completion_n: e.target.value }))}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* SUB-STAGES */}
                    <div className="t2t-props-sec">
                      <div className="t2t-props-sec-title">Sub-stages</div>
                      <p style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginBottom: 10, lineHeight: 1.5 }}>
                        Create sub-stages to break this stage into steps. Each sub-stage has its own canvas.
                      </p>
                      <button
                        className="t2t-btn t2t-btn-outline t2t-btn-sm"
                        style={{ width: "100%" }}
                        onClick={() => addSubStageAndDrill(selected.id, selectedNode?.label)}
                      >
                        ⊞ Add Sub-stage &amp; Edit
                      </button>
                    </div>

                    {/* REJECTION ROUTING (Phase 9) */}
                    <div className="t2t-props-sec">
                      <div className="t2t-props-sec-title">Rejection Routing</div>
                      <p style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginBottom: 8, lineHeight: 1.5 }}>
                        {rejectionRules.length === 0
                          ? "No rules — rejectors can return to any stage. Add rules to restrict allowed return targets."
                          : `${rejectionRules.length} rule${rejectionRules.length > 1 ? "s" : ""} — rejectors can only return to these stages.`}
                      </p>
                      {rejectionRules.map((rule) => {
                        const targetNode = nodes.find((n) => String(n.id) === String(rule.allowed_return_stage_id));
                        return (
                          <div key={rule.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontSize: 12, background: "#F8FAFC", padding: "6px 8px", borderRadius: 6, border: "1px solid #E2E8F0" }}>
                            <span style={{ flex: 1 }}>
                              ↩ {rule.label || targetNode?.label || "Stage " + rule.allowed_return_stage_id}
                            </span>
                            <button
                              onClick={() => removeRejectionRule(rule.id, selected.id)}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--t2t-danger)", fontSize: 14, padding: "0 4px" }}
                            >✕</button>
                          </div>
                        );
                      })}
                      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                        <select
                          className="t2t-fc"
                          style={{ flex: 1, fontSize: 12 }}
                          id="rej-rule-target"
                          defaultValue=""
                        >
                          <option value="">Select return stage...</option>
                          {nodes
                            .filter((n) => n.id !== selected.id && n.type !== "end" && !rejectionRules.some((r) => String(r.allowed_return_stage_id) === String(n.id)))
                            .map((n) => <option key={n.id} value={n.id}>{n.label}</option>)
                          }
                        </select>
                        <button
                          className="t2t-btn t2t-btn-outline t2t-btn-sm"
                          style={{ fontSize: 11, flexShrink: 0 }}
                          onClick={() => {
                            const sel = document.getElementById("rej-rule-target");
                            if (sel?.value) {
                              addRejectionRule(selected.id, sel.value);
                              sel.value = "";
                            }
                          }}
                        >+ Add</button>
                      </div>
                    </div>

                    {/* FIELD VISIBILITY */}
                    <div className="t2t-props-sec">
                      <div className="t2t-props-sec-title">Field Visibility</div>
                      {formFields.length === 0 ? (
                        <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", lineHeight: 1.5 }}>
                          No form fields yet. Add fields in the <strong>Form Fields</strong> tab.
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginBottom: 8, lineHeight: 1.5 }}>
                            Control which fields users see and can edit at this stage. Hidden fields are not shown to the assignee.
                          </div>
                          {/* Table header */}
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 56px 56px", gap: "0 4px", marginBottom: 4 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-secondary)" }}>Field</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-secondary)", textAlign: "center" }}>Visible</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t2t-text-secondary)", textAlign: "center" }}>Editable</div>
                          </div>
                          {formFields.map((ff) => {
                            const fvVisible  = propsForm.fv_visible  || [];
                            const fvEditable = propsForm.fv_editable || [];
                            // Empty visible list = all visible (default)
                            const isVisible  = fvVisible.length === 0 || fvVisible.includes(String(ff.id));
                            const isEditable = isVisible && fvEditable.includes(String(ff.id));

                            const toggleVisible = (checked) => {
                              setPropsForm((p) => {
                                const cur    = p.fv_visible || [];
                                const allIds = formFields.map((f) => String(f.id));
                                let newVis;
                                if (checked) {
                                  // Add to visible, remove from editable if was hidden
                                  newVis = cur.includes(String(ff.id)) ? cur : [...cur, String(ff.id)];
                                  // If all are now visible, clear the list (means "all visible")
                                  if (allIds.every((id) => newVis.includes(id))) newVis = [];
                                } else {
                                  // Remove from visible — if list was empty, populate with all except this
                                  const base = cur.length === 0 ? allIds : cur;
                                  newVis = base.filter((id) => id !== String(ff.id));
                                }
                                // Also remove from editable if now hidden
                                const newEdit = checked ? (p.fv_editable || []) : (p.fv_editable || []).filter((id) => id !== String(ff.id));
                                return { ...p, fv_visible: newVis, fv_editable: newEdit };
                              });
                            };

                            const toggleEditable = (checked) => {
                              setPropsForm((p) => {
                                const cur = p.fv_editable || [];
                                return { ...p, fv_editable: checked ? [...cur, String(ff.id)] : cur.filter((id) => id !== String(ff.id)) };
                              });
                            };

                            return (
                              <div key={ff.id} style={{ display: "grid", gridTemplateColumns: "1fr 56px 56px", gap: "0 4px", alignItems: "center", padding: "4px 0", borderBottom: "1px solid var(--t2t-border)" }}>
                                <div style={{ fontSize: 12, color: "var(--t2t-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={ff.label}>
                                  {ff.label}
                                  {ff.required && <span style={{ color: "var(--t2t-danger)", marginLeft: 2 }}>*</span>}
                                </div>
                                <div style={{ textAlign: "center" }}>
                                  <input type="checkbox" checked={isVisible} onChange={(e) => toggleVisible(e.target.checked)} />
                                </div>
                                <div style={{ textAlign: "center" }}>
                                  <input type="checkbox" checked={isEditable} disabled={!isVisible} onChange={(e) => toggleEditable(e.target.checked)}
                                    title={!isVisible ? "Field must be visible to be editable" : ""} />
                                </div>
                              </div>
                            );
                          })}
                          <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginTop: 6 }}>
                            All fields visible by default. Uncheck Visible to hide. Editable requires Visible.
                          </div>
                        </>
                      )}
                    </div>

                    {/* SAVE */}
                    <div className="t2t-props-sec">
                      <button className="t2t-btn t2t-btn-primary" style={{ width: "100%" }} onClick={saveNodeProps} disabled={saving}>
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>

                    {/* DELETE */}
                    <div className="t2t-props-sec">
                      <button
                        onClick={() => deleteNode(selected.id)}
                        style={{ width: "100%", padding: "8px", borderRadius: 6, border: "none", background: "#EF4444", color: "white", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                      >
                        Delete Stage
                      </button>
                    </div>
                  </>
                )}

                {/* Connection props */}
                {selected.type === "conn" && selectedConn && (
                  <>
                    <div className="t2t-props-sec">
                      <div className="t2t-props-sec-title">Transition Info</div>
                      <div style={{ fontSize: 12, color: "var(--t2t-text-secondary)", marginBottom: 12, fontWeight: 600 }}>
                        {nodes.find((n) => n.id === selectedConn.from)?.label}
                        <span style={{ margin: "0 6px", color: "var(--t2t-text-muted)" }}>→</span>
                        {nodes.find((n) => n.id === selectedConn.to)?.label}
                      </div>
                      <div className="t2t-fg">
                        <label>Trigger Action</label>
                        <select className="t2t-fc" value={propsForm.action || "approve"} onChange={(e) => setPropsForm((p) => ({ ...p, action: e.target.value }))}>
                          <option value="approve">Approve</option>
                          <option value="reject">Reject</option>
                          <option value="submit">Submit</option>
                        </select>
                      </div>
                      <div className="t2t-fg">
                        <label>Arrow Label <span style={{ fontWeight: 400, color: "var(--t2t-text-muted)" }}>(optional)</span></label>
                        <input className="t2t-fc" placeholder="e.g. Approved, Rejected..." value={propsForm.label || ""} onChange={(e) => setPropsForm((p) => ({ ...p, label: e.target.value }))} />
                      </div>
                    </div>
                    {/* CONDITIONS */}
                    <div className="t2t-props-sec">
                      <div className="t2t-props-sec-title">Conditions</div>
                      {selectedConn.conditions?.rules?.length > 0 ? (
                        <>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, padding: "6px 10px", borderRadius: 6, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#16A34A" }}>✓ {selectedConn.conditions.rules.length} condition{selectedConn.conditions.rules.length > 1 ? "s" : ""}</span>
                            <span style={{ fontSize: 11, color: "#475569" }}>— {buildCondPreview(selectedConn.conditions)}</span>
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={() => openCondModal(selectedConn)}>✎ Edit Conditions</button>
                            <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ color: "var(--t2t-danger)" }}
                              onClick={() => setConns((prev) => prev.map((c) => c.id === selected.id ? { ...c, conditions: null } : c))}>✕ Clear</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginBottom: 8 }}>No conditions — this transition always fires.</div>
                          <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={() => openCondModal(selectedConn)}>+ Add Conditions</button>
                        </>
                      )}
                    </div>

                    <div className="t2t-props-sec">
                      <button className="t2t-btn t2t-btn-primary" style={{ width: "100%" }} onClick={saveConnProps} disabled={saving}>
                        {saving ? "Saving..." : "Save"}
                      </button>
                    </div>
                    <div className="t2t-props-sec">
                      <button onClick={() => deleteConn(selected.id)}
                        style={{ width: "100%", padding: "8px", borderRadius: 6, border: "none", background: "#EF4444", color: "white", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                        Delete Arrow
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* FORM FIELDS TAB — view-only; fields are managed in Admin → Request Types */}
        {activeTab === "form" && (
          <div className="t2t-builder-body" style={{ flex: 1, overflow: "hidden" }}>
            <div className="t2t-fb-list" style={{ flex: 1, overflowY: "auto" }}>
              <div style={{ padding: "20px 24px 12px" }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: "var(--t2t-text)" }}>Intake Form Fields</div>
                <div style={{ fontSize: 13, color: "var(--t2t-text-muted)", marginTop: 3 }}>
                  Fields are managed in <strong>Admin → Request Types</strong>. Attach a Request Type above to define the form.
                </div>

                {/* Type tabs — shown when 2+ types are attached */}
                {attachedRequestTypesAll.length > 1 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 14 }}>
                    {attachedRequestTypesAll.map(rt => (
                      <button
                        key={rt.request_type_id}
                        type="button"
                        onClick={() => setActiveRtTabId(rt.request_type_id)}
                        style={{
                          padding: "5px 14px", fontSize: 12, borderRadius: 20, border: "1px solid",
                          cursor: "pointer", fontWeight: 600, transition: "all .15s",
                          borderColor: activeRtTabId === rt.request_type_id ? "var(--t2t-primary)" : "#CBD5E1",
                          background: activeRtTabId === rt.request_type_id ? "var(--t2t-primary)" : "white",
                          color: activeRtTabId === rt.request_type_id ? "white" : "var(--t2t-text)",
                        }}
                      >
                        {rt.name}
                        {rt.is_system === 1 && (
                          <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 700,
                            color: activeRtTabId === rt.request_type_id ? "rgba(255,255,255,.8)" : "#1B3A6B",
                            background: activeRtTabId === rt.request_type_id ? "rgba(255,255,255,.2)" : "#DBEAFE",
                            padding: "1px 5px", borderRadius: 4 }}>SYS</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Field list — always read-only */}
              {(() => {
                const activeType = attachedRequestTypesAll.length > 0
                  ? (attachedRequestTypesAll.find(rt => rt.request_type_id === activeRtTabId) || attachedRequestTypesAll[0])
                  : null;
                const displayFields = activeType
                  ? (Array.isArray(activeType.fields) ? activeType.fields : [])
                  : [];

                if (displayFields.length === 0) {
                  return (
                    <div className="t2t-empty-state">
                      <div className="t2t-empty-icon">📋</div>
                      <div className="t2t-empty-title">
                        {attachedRequestTypesAll.length === 0 ? "No request type attached" : "No fields defined yet"}
                      </div>
                      <div className="t2t-empty-desc">
                        {attachedRequestTypesAll.length === 0
                          ? "Select a Request Type above to see its form fields here."
                          : "Add fields to this Request Type in Admin → Request Types."}
                      </div>
                    </div>
                  );
                }

                return (
                  <div style={{ padding: "0 16px 20px" }}>
                    {displayFields.map((ff) => {
                      const ft = FIELD_TYPES.find((f) => f.type === (ff.field_type || ff.type));
                      return (
                        <div key={ff.field_id || ff.id} style={{
                          display: "flex", alignItems: "center", gap: 14,
                          padding: "12px 16px", marginBottom: 8, borderRadius: 8,
                          border: "1px solid var(--t2t-border)", background: "white",
                        }}>
                          <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--t2t-bg)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, flexShrink: 0 }}>
                            {ft?.icon || "T"}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: "var(--t2t-text)", display: "flex", alignItems: "center", gap: 5 }}>
                              {ff.label}
                              {(ff.required || ff.is_required) && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#EF4444", display: "inline-block", flexShrink: 0 }} />}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginTop: 2 }}>
                              {ft?.label?.toLowerCase() || (ff.field_type || ff.type)}
                              {ff.field_key ? ` • key: ${ff.field_key}` : (ff.field_id ? ` • id: ${ff.field_id}` : "")}
                            </div>
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 700, color: "#64748B", background: "#F1F5F9", padding: "2px 8px", borderRadius: 10, flexShrink: 0 }}>READ-ONLY</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Field editing removed — manage fields in Admin → Request Types */}
          </div>
        )}
      </div>

      {/* Publish Modal */}
      {publishModal && (
        <div className="t2t-overlay" onClick={() => setPublishModal(false)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()}>
            <div className="t2t-modal-head">
              <Hint id="publish.versioning" position="bottom">
                <div>
                  <div className="t2t-modal-title">✓ Publish Workflow</div>
                  <div className="t2t-modal-sub">This will make the workflow available for submissions.</div>
                </div>
              </Hint>
              <button className="t2t-close-btn" onClick={() => setPublishModal(false)}>×</button>
            </div>
            <div className="t2t-modal-body">
              <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 12 }}>
                Publishing <strong>{wfName}</strong> with <strong>{nodes.length} stages</strong> and <strong>{formFields.length} form fields</strong>.
              </p>
              <div className="t2t-fg">
                <label>Version Note <span style={{ fontWeight: 400, color: "var(--t2t-text-muted)" }}>(optional)</span></label>
                <input className="t2t-fc" placeholder="e.g. Added escalation for finance stage" value={publishNote} onChange={(e) => setPublishNote(e.target.value)} />
              </div>
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setPublishModal(false)}>Cancel</button>
              <button className="t2t-btn t2t-btn-primary" onClick={handlePublish} disabled={saving}>
                {saving ? "Publishing..." : "✓ Publish"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transition Conditions Modal */}
      {condModal.open && (
        <div className="t2t-overlay" onClick={() => setCondModal((p) => ({ ...p, open: false }))}>
          <div className="t2t-modal" style={{ width: 600 }} onClick={(e) => e.stopPropagation()}>
            <div className="t2t-modal-head">
              <Hint id="conditions.intro" position="bottom">
                <div>
                  <div className="t2t-modal-title">Transition Conditions</div>
                  <div className="t2t-modal-sub">Define when this arrow fires</div>
                </div>
              </Hint>
              <button className="t2t-close-btn" onClick={() => setCondModal((p) => ({ ...p, open: false }))}>×</button>
            </div>
            <div className="t2t-modal-body">
              {/* Info bar */}
              <div style={{ padding: "10px 14px", borderRadius: 6, background: "#EFF6FF", border: "1px solid #BFDBFE", marginBottom: 16, fontSize: 13, color: "#1E40AF" }}>
                This arrow fires from <strong>{condModal.fromLabel}</strong> to <strong>{condModal.toLabel}</strong> when...
              </div>

              {/* Condition builder */}
              {formFields.length === 0 ? (
                <div style={{ padding: "14px", borderRadius: 6, background: "#FFF7ED", border: "1px solid #FED7AA", fontSize: 13, color: "#92400E" }}>
                  ⚠ No form fields defined yet. Go to the <strong>Form Fields</strong> tab, add your fields and save them first — then conditions will let you pick a field.
                </div>
              ) : renderCondGroup(
                condModal.conditions,
                (updated) => setCondModal((p) => ({ ...p, conditions: updated }))
              )}

              {/* Plain English Preview */}
              {condModal.conditions?.rules?.length > 0 && (
                <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 6, background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#16A34A", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Plain English Preview</div>
                  <div style={{ fontSize: 13, color: "#166534" }}>{buildCondPreview(condModal.conditions)}</div>
                </div>
              )}
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setCondModal((p) => ({ ...p, open: false }))}>Cancel</button>
              <button className="t2t-btn t2t-btn-primary" onClick={saveConditions}>Save Conditions</button>
            </div>
          </div>
        </div>
      )}

      {/* Test Workflow Modal */}
      {testModal.open && (
        <div className="t2t-overlay" onClick={() => setTestModal((p) => ({ ...p, open: false }))}>
          <div className="t2t-modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="t2t-modal-head">
              <div>
                <div className="t2t-modal-title">▶ Test Workflow</div>
                <div className="t2t-modal-sub">Enter values to simulate routing decisions</div>
              </div>
              <button className="t2t-close-btn" onClick={() => setTestModal((p) => ({ ...p, open: false }))}>×</button>
            </div>
            <div className="t2t-modal-body">
              {formFields.length > 0 ? (
                <>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--t2t-text-secondary)", marginBottom: 10 }}>Form Values</div>
                  {formFields.map((ff) => (
                    <div className="t2t-fg" key={ff.id}>
                      <label>{ff.label}{ff.required && <span style={{ color: "var(--t2t-danger)" }}> *</span>}</label>
                      {ff.type === "dropdown" ? (
                        <select
                          className="t2t-fc"
                          value={testModal.values[ff.id] || ""}
                          onChange={(e) => setTestModal((p) => ({ ...p, values: { ...p.values, [ff.id]: e.target.value } }))}
                        >
                          <option value="">Select {ff.label}...</option>
                          {(ff.options || []).map((opt) => {
                            const val = typeof opt === "object" ? (opt.value || opt.label || "") : opt;
                            const lbl = typeof opt === "object" ? (opt.label || opt.value || "") : opt;
                            return <option key={val} value={val}>{lbl}</option>;
                          })}
                        </select>
                      ) : ff.type === "yes_no" || ff.type === "boolean" || ff.type === "checkbox" ? (
                        <select
                          className="t2t-fc"
                          value={testModal.values[ff.id] || ""}
                          onChange={(e) => setTestModal((p) => ({ ...p, values: { ...p.values, [ff.id]: e.target.value } }))}
                        >
                          <option value="">Select...</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      ) : (
                        <input
                          className="t2t-fc"
                          type={ff.type === "number" || ff.type === "currency" ? "number" : ff.type === "date" ? "date" : "text"}
                          placeholder={`Enter ${ff.label}`}
                          value={testModal.values[ff.id] || ""}
                          onChange={(e) => setTestModal((p) => ({ ...p, values: { ...p.values, [ff.id]: e.target.value } }))}
                        />
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <div style={{ fontSize: 13, color: "var(--t2t-text-muted)", marginBottom: 12 }}>
                  No form fields defined. Simulation will trace the approval path based on stage connections.
                </div>
              )}

              {/* Simulation Result */}
              {testModal.result && (
                <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: testModal.result.error ? "#FEF2F2" : "#F0FDF4", border: `1px solid ${testModal.result.error ? "#FECACA" : "#BBF7D0"}` }}>
                  {testModal.result.error ? (
                    <div style={{ fontSize: 13, color: "#DC2626" }}>{testModal.result.error}</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#16A34A", marginBottom: 10 }}>✓ Simulation Result</div>
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                        {testModal.result.path.map((step, i) => (
                          <React.Fragment key={step.id}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: step.color + "22", color: step.color, border: `1px solid ${step.color}44` }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: step.color, display: "inline-block" }} />
                              {step.label}
                            </span>
                            {i < testModal.result.path.length - 1 && (
                              <span style={{ color: "#94A3B8", fontSize: 14 }}>→</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      {testModal.result.debugLog?.length > 0 && (
                        <div style={{ marginTop: 10, padding: "8px 10px", background: "#F8FAFC", borderRadius: 6, border: "1px solid #E2E8F0" }}>
                          {testModal.result.debugLog.map((line, i) => (
                            <div key={i} style={{ fontSize: 11, color: line.startsWith("⚠") ? "#DC2626" : "#475569", fontFamily: "monospace", lineHeight: 1.6 }}>{line}</div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setTestModal((p) => ({ ...p, open: false }))}>Close</button>
              <button className="t2t-btn t2t-btn-primary" onClick={runSimulation} disabled={testModal.simulating}>
                {testModal.simulating ? "Simulating..." : "▶ Simulate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── History Modal ── */}
      {historyModal.open && (
        <div className="t2t-overlay" onClick={() => setHistoryModal((p) => ({ ...p, open: false }))}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()}>
            <div className="t2t-modal-head">
              <Hint id="publish.history" position="bottom">
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>Version History — {wfName}</div>
                  <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginTop: 2 }}>All published versions of this workflow</div>
                </div>
              </Hint>
              <button className="t2t-modal-close" onClick={() => setHistoryModal((p) => ({ ...p, open: false }))}>✕</button>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
              {historyModal.loading ? (
                <div style={{ textAlign: "center", padding: 40, color: "var(--t2t-text-muted)" }}>Loading versions…</div>
              ) : historyModal.versions.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "var(--t2t-text-muted)" }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>No versions yet</div>
                  <div style={{ fontSize: 13 }}>Publish the workflow to create the first version snapshot.</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {historyModal.versions.map((v, i) => {
                    const isCurrent = i === 0;
                    const vNum      = historyModal.versions.length - i;
                    const dateStr   = v.published_at
                      ? new Date(v.published_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                      : "—";
                    const stages    = v.stage_count ?? v.stages_count ?? 0;
                    const trans     = v.transition_count ?? v.transitions_count ?? 0;
                    const author    = v.published_by_name || v.created_by_name || "—";
                    const note      = v.publish_note || v.version_note || "";
                    return (
                      <div
                        key={v.version_id || i}
                        style={{
                          border: `1px solid ${isCurrent ? "#BBF7D0" : "#E2E8F0"}`,
                          borderRadius: 10,
                          padding: "14px 16px",
                          background: isCurrent ? "#F0FDF4" : "#FFFFFF",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, background: isCurrent ? "#16A34A" : "#E2E8F0", color: isCurrent ? "#fff" : "#64748B", borderRadius: 20, padding: "2px 10px" }}>
                            v{vNum}
                          </span>
                          {v.version_name && (
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{v.version_name}</span>
                          )}
                          {isCurrent && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#16A34A", background: "#DCFCE7", borderRadius: 20, padding: "2px 8px", border: "1px solid #BBF7D0" }}>CURRENT</span>
                          )}
                        </div>
                        {note && (
                          <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>{note}</div>
                        )}
                        <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#64748B" }}>
                          <span>🕐 {dateStr}</span>
                          <span>👤 {author}</span>
                          {(stages > 0 || trans > 0) && (
                            <span>⬡ {stages} stages, {trans} transitions</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Info callout */}
              <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 8, background: "#EFF6FF", border: "1px solid #BFDBFE", fontSize: 12, color: "#1E40AF", lineHeight: 1.6 }}>
                ℹ️ Each <strong>Publish</strong> creates a new version snapshot. In-flight requests continue on their original version until completion.
              </div>
            </div>

            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setHistoryModal((p) => ({ ...p, open: false }))}>Close</button>
              <button
                className="t2t-btn t2t-btn-primary"
                onClick={() => {
                  setHistoryModal((p) => ({ ...p, open: false }));
                  setPublishModal(true);
                }}
              >
                + Publish New Version
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Health Check Modal ── */}
      {healthModal.open && (
        <div className="t2t-overlay" onClick={() => setHealthModal((p) => ({ ...p, open: false }))}>
          <div className="t2t-modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="t2t-modal-head">
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>
                  {healthModal.issues.some((i) => i.severity === "critical") ? "🔴" : "🟡"} Workflow Health Check
                </div>
                <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginTop: 2 }}>
                  Fix these issues before publishing
                </div>
              </div>
              <button className="t2t-modal-close" onClick={() => setHealthModal((p) => ({ ...p, open: false }))}>✕</button>
            </div>

            <div className="t2t-modal-body">
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {healthModal.issues.map((issue, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 10,
                      padding: "10px 14px",
                      borderRadius: 8,
                      background: issue.severity === "critical" ? "#FEF2F2" : "#FFFBEB",
                      border: `1px solid ${issue.severity === "critical" ? "#FECACA" : "#FDE68A"}`,
                    }}
                  >
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{issue.severity === "critical" ? "🔴" : "🟡"}</span>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: issue.severity === "critical" ? "#991B1B" : "#92400E", marginBottom: 2 }}>
                        {issue.severity === "critical" ? "Critical" : "Warning"}
                      </div>
                      <div style={{ fontSize: 13, color: issue.severity === "critical" ? "#7F1D1D" : "#78350F", lineHeight: 1.5 }}>
                        {issue.msg}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {healthModal.proceedToPublish && (
                <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: "#FFFBEB", border: "1px solid #FDE68A", fontSize: 12, color: "#92400E" }}>
                  ⚠️ There are warnings but no critical issues. You can still publish, but consider fixing the warnings first.
                </div>
              )}
              {!healthModal.proceedToPublish && (
                <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", fontSize: 12, color: "#991B1B" }}>
                  🔴 Critical issues must be resolved before publishing.
                </div>
              )}
            </div>

            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-outline" onClick={() => {
                let fixed = 0;

                // 1. Remove orphaned stages (no connections at all)
                const connectedIds = new Set([...conns.map((c) => c.from), ...conns.map((c) => c.to)]);
                const orphans = nodes.filter((n) => !connectedIds.has(n.id));
                if (orphans.length > 0) {
                  setNodes((prev) => prev.filter((n) => connectedIds.has(n.id)));
                  orphans.forEach((o) => { if (wfId && wfId !== "new") deleteData(`t2t/workflows/${wfId}/stages/${o.id}`); });
                  fixed += orphans.length;
                }

                // 2. Fix duplicate stage names — append number
                const nameCounts = {};
                const updatedNodes = [...nodes].filter((n) => connectedIds.has(n.id));
                updatedNodes.forEach((n) => {
                  const key = (n.label || "").trim().toLowerCase();
                  nameCounts[key] = (nameCounts[key] || 0) + 1;
                  if (nameCounts[key] > 1) {
                    n.label = `${n.label} ${nameCounts[key]}`;
                    fixed++;
                  }
                });
                if (fixed > 0) setNodes(updatedNodes);

                setHealthModal((p) => ({ ...p, open: false }));
                if (fixed > 0) toast(`Auto-fixed ${fixed} issue(s). Review the canvas and save.`, "success");
                else toast("No auto-fixable issues found. Fix remaining issues manually on the canvas.", "warning");
              }}>
                Auto-Fix
              </button>
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setHealthModal((p) => ({ ...p, open: false }))}>
                Close
              </button>
              {healthModal.proceedToPublish && (
                <button
                  className="t2t-btn t2t-btn-primary"
                  onClick={() => {
                    setHealthModal((p) => ({ ...p, open: false }));
                    setPublishModal(true);
                  }}
                >
                  Publish Anyway
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Guidance — Welcome Overlay (first visit only) */}
      <T2TWelcomeOverlay
        onChooseWizard={() => setWizardOpen(true)}
        onChooseTemplates={() => setGalleryOpen(true)}
        onChooseTutorial={() => setTutorialActive(true)}
        onSkip={() => { /* just closes */ }}
      />

      {/* Guidance — Guided Wizard (6 steps) */}
      <T2TGuidedWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onComplete={(newWfId) => { setWizardOpen(false); if (newWfId) navigate(`/t2t-builder/${newWfId}`); }}
      />

      {/* Guidance — Template Gallery */}
      <T2TTemplateGallery
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onSelectTemplate={(newWfId) => navigate(`/t2t-builder/${newWfId}`)}
        onStartBlank={() => { /* already on canvas */ }}
      />

      {/* Guidance — Interactive Tutorial */}
      <T2TTutorial
        active={tutorialActive}
        onClose={() => setTutorialActive(false)}
        onComplete={() => setTutorialActive(false)}
      />

      {/* Help Panel */}
      <T2THelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} onOpenGallery={() => setGalleryOpen(true)} onOpenWizard={() => setWizardOpen(true)} onOpenTutorial={() => setTutorialActive(true)} />
    </>
  );
};

// Wrap T2TBuilder with GuidanceProvider
const T2TBuilderWithGuidance = () => (
  <T2TGuidanceProvider>
    <HintQueueProvider>
      <T2TBuilder />
    </HintQueueProvider>
  </T2TGuidanceProvider>
);

export default T2TBuilderWithGuidance;
